// Entrada y salida del MOTOR (cálculo puro, sin IA ni red): periodos consolidados, 3 opciones (arranque ~60 %,
// exacta 100 % recomendada, holgada ~115 %), precio por tabla + fijos + adicionales, rentabilidad por reglas, FINSUS de
// referencia (ADENDA A0) y, desde el contrato v2, el respaldo con baterías.
import { z } from 'zod';
import { Facturacion, Fecha, FechaHora, NivelRespaldo, OpcionId, Pesos, Tarifa, Texto } from './basicos.ts';
import { Aviso, esAvisoFinsus } from './avisos.ts';
import { OrigenPeriodo } from './recibo.ts';
import { CargaRespaldo, FASES_SERVICIO, SIN_PANELES_RECORTE, TENSIONES_SERVICIO, TIPOS_ESTRUCTURA } from './cuestionario.ts';
import { GeneracionCalculada, GeneracionCotizacion } from './generacion.ts';
import { PrecioOptimus } from './optimus.ts';
import { PromoCotizacion } from './promo.ts';
import { FINSUS_REFERENCIA, NIVEL_DE_OPCION, HORAS_OBJETIVO_DE_OPCION, MARCAS_INVERSOR_RED, RECORTE_PUNTA_REGLAS } from '../constantes.ts';

// Si alguien agrega una opción o un nivel, esto no compila hasta que la liga opción → nivel lo diga (ruidoso a propósito).
NIVEL_DE_OPCION satisfies Readonly<Record<OpcionId, NivelRespaldo>>;
HORAS_OBJETIVO_DE_OPCION satisfies Readonly<Record<OpcionId, number>>;
/** El inverso de NIVEL_DE_OPCION (esencial → arranque…). */
export const OPCION_DE_NIVEL: Readonly<Record<NivelRespaldo, OpcionId>> = Object.freeze(
  Object.fromEntries(Object.entries(NIVEL_DE_OPCION).map(([o, n]) => [n, o])) as Record<NivelRespaldo, OpcionId>,
);
import { numero, pesos } from '../dinero.ts';

export const Panel = z.strictObject({ marca: Texto(2, 40), modelo: Texto(2, 60), w: z.number().positive().max(1_000) });
export type Panel = z.infer<typeof Panel>;

export const InversorElegido = z.strictObject({
  marca: Texto(2, 40),
  modelo: Texto(2, 40),
  kw: z.number().positive(),
  cantidad: z.int().min(1),
});
export type InversorElegido = z.infer<typeof InversorElegido>;

/** LEGADO (antes de la ADENDA A0 del 10-oct-2026): cotización de FINSUS COPIADA de su PDF. Desde A0 Diego no quiere
 *  cotizaciones de FINSUS: el motor calcula SIEMPRE la mensualidad de referencia (OpcionCalculada.finsus_referencia),
 *  el motor IGNORA esta lista (ni avisos ni comprobación) y el puente manda `finsus: null` al PDF. Se conserva solo
 *  para que los borradores ya guardados sigan validando; la interfaz ya no la ofrece. */
export const FinsusCopiado = z.strictObject({
  opcion: OpcionId,
  precio_base: Pesos,
  enganche: Pesos,
  mensualidad: Pesos,
  plazo_meses: z.literal(60),
  cat_pct: z.number().positive().max(80),
  cat_fecha: Fecha,
  folio_finsus: z.string().trim().max(60).nullable(),
  /** El PDF de FINSUS subido a la cotización (clase "finsus"). */
  archivo_id: z.string().nullable(),
});
export type FinsusCopiado = z.infer<typeof FinsusCopiado>;

export const AjusteOpcion = z.strictObject({
  opcion: OpcionId,
  /** Forzar número de paneles (p. ej. no caben más en el techo). */
  paneles: z.int().min(1).max(20_000).nullable(),
  /** Forzar el precio de lista sin IVA de la opción (queda aviso AJUSTE_MANUAL con el motivo). Con PRECIO ÚNICO (comercio o
   *  industria arriba de $1 M de contado de tabla; DesglosePrecio.precio_unico) es el precio único sin IVA, el mismo que ve
   *  el vendedor. El motor decide el modo con el contado QUE SE IMPRIME, después de este ajuste (Diego, 16-oct, ADENDA
   *  z22-2): bajarlo del umbral lo regresa a lista y 15 %, subirlo arriba lo pasa a precio único; el contado es el mismo. */
  precio_lista_sin_iva: Pesos.nullable(),
  motivo: Texto(3, 300),
  /** Diego, 13-oct (pedido 2): el % del recibo de hoy que el cliente quiere ahorrarse (ahorro del año ÷ lo que paga hoy al
   *  año; en GDMTO/GDMTH con la baja de demanda de CFE). El MOTOR busca los paneles mínimos que lo dan (o el máximo que se
   *  alcanza, con aviso). Excluyente con `paneles`. Ausente/null = no se pidió (cotizaciones de antes). */
  ahorro_pct: z.number().min(1).max(100).nullable().optional(),
  /** Diego, 13-oct (19:05, punto q): los paneles que tenía la opción cuando se escribió `precio_lista_sin_iva` (lo pone el
   *  SERVIDOR al recalcular). Si el tamaño cambia por cualquier lado, el precio a mano se suelta (recalculo.ts). Ausente =
   *  ajuste de antes o sin precio: se compara con el cálculo guardado. */
  paneles_del_precio: z.int().min(1).max(20_000).nullable().optional(),
  /** z16/z23 (20-oct, regla de fondo): estos `paneles` NO los escribió el vendedor: son los que eligió el cotizador por su
   *  retorno (casa 1F) y se guardaron porque el vendedor escribió SOLO el precio de esa opción (aplicarEdiciones). El motor
   *  los trata como de la búsqueda (sin «puestos a mano»; «Mejor retorno» si con los precios finales se paga antes) y «Precio
   *  de tabla» los suelta junto con el ajuste. Ausente/null = paneles escritos a mano (o sin paneles), como siempre. */
  paneles_de_retorno: z.literal(true).nullable().optional(),
}).superRefine((a, ctx) => {
  if (a.paneles !== null && a.ahorro_pct !== null && a.ahorro_pct !== undefined)
    ctx.addIssue({ code: 'custom', path: ['ahorro_pct'], message: 'Escribe los paneles o el % de ahorro, no los dos: con el % el cotizador busca los paneles.' });
  if (a.paneles_de_retorno === true && a.paneles === null)
    ctx.addIssue({ code: 'custom', path: ['paneles_de_retorno'], message: 'Los paneles del retorno van con su número de paneles.' });
});
export type AjusteOpcion = z.infer<typeof AjusteOpcion>;

export const FormasDePago = z.strictObject({
  contado: z.literal(true),
  etapas: z.boolean(),
  msi: z.array(z.union([z.literal(3), z.literal(6), z.literal(12)])),
  /** true = se enseña la mensualidad de REFERENCIA de FINSUS (ADENDA A0), calculada para cualquier monto. */
  finsus: z.boolean(),
  arrendamiento: z.boolean(),
  deduccion: z.boolean(),
});
export type FormasDePago = z.infer<typeof FormasDePago>;

/** ADENDA A0b: el tipo de cambio CONGELADO de un folio (FIX + colchón del día en que se creó). Las versiones del mismo
 *  folio lo heredan; un folio nuevo (o uno vencido, al recotizar) toma el FIX nuevo. Nunca se recotiza en silencio. */
export const TipoCambioFolio = z.strictObject({
  /** Pesos por dólar con el colchón: lo que usa el precio. */
  valor: z.number().min(5).max(70),
  fix: z.number().min(5).max(60),
  fecha_fix: Fecha,
  colchon_pct: z.number().min(0).max(20),
  /** Día (Hermosillo) en que se congeló. */
  congelado_el: Fecha,
});
export type TipoCambioFolio = z.infer<typeof TipoCambioFolio>;

/** Módulos de batería de UN nivel de respaldo que fijó el vendedor: null = automático; 0 = quitar el nivel. */
const ModulosNivel = z.int().min(0).max(64).nullable();
/** Horas de respaldo de un nivel que escribió el vendedor (z48-2); la plantilla no promete más de 72 h. */
const HorasNivel = z.number().positive().max(72).nullable();

/** Lo que el vendedor decide (con valores por omisión que pone el servidor al crear el borrador). */
export const ParametrosCalculo = z.strictObject({
  panel: Panel,
  opciones_activas: z.array(OpcionId).min(1).max(3),
  recomendada: OpcionId,
  /** Cobertura que busca cada opción (fracción: 1.3 = 130 %). Diego, 12-oct: el vendedor la escribe en "Ajustes del
   *  cálculo" (de 20 a 200 %); fuera de la banda habitual (BANDA_COBERTURA) es aviso, no error. */
  coberturas_objetivo: z.strictObject({ arranque: z.number().min(0.2).max(2), exacta: z.number().min(0.2).max(2), holgada: z.number().min(0.2).max(2) }),
  /** z16/z23 (verificación adversarial del 20-oct): las opciones cuya cobertura ESCRIBIÓ alguien (vendedor o IA), aunque sea
   *  igual a la de omisión (un 60 % escrito en el Arranque se respeta y no se cambia por el «Mejor retorno» de casa 1F). Lo pone
   *  aplicarEdiciones al editar /parametros/coberturas_objetivo; ausente = nadie la escribió (cotizaciones de antes). */
  coberturas_escritas: z.array(OpcionId).max(3).optional(),
  /** Diego, 20-oct (ADENDA z24): la PRIMERA opción («Arranque», o «Mejor retorno» en casa 1F) lleva los MISMOS inversores que
   *  la Exacta (listo para ampliar), si sus paneles arman cadenas en ellos; el precio sube solo por la diferencia del
   *  inversor. Ausente o false = apagado (como siempre). No aplica en multisitio ni con baterías, aislado o inversor actual.
   *  Diego, 26-oct: las cotizaciones NUEVAS de casa, comercio e industria nacen con true (parametrosIniciales); las de antes
   *  no traen el campo y no cambian solas. */
  inversor_de_exacta: z.boolean().optional(),
  /** ADENDA z44 (Diego, 27-oct-2026): casa de paneles solos SIEMPRE con 3 opciones. Si ningún número de paneles cae en la banda
   *  de la Exacta, va el más cercano a su cobertura (entre el de abajo y el de arriba; empate: el de arriba); si la Holgada no
   *  cabe en la suya, va con un panel más que la Exacta. Cada una con su amarillo. Nace con true en las cotizaciones NUEVAS de
   *  casa (parametrosIniciales; el multisitio no); ausente = las de antes, que al recalcular no cambian de precio ni de opciones. */
  tres_opciones_casa: z.boolean().optional(),
  /** ADENDA z53-4 (Diego, 28-oct-2026): la MARCA de los inversores de red de TODAS las opciones (MARCAS_INVERSOR_RED). Ausente o
   *  null = Growatt, la de omisión (como siempre). Otra marca: el motor busca la combinación más barata de ESA marca y el precio
   *  sube solo por lo que cuesta de más contra la Growatt (regla de extras de z24); si cuesta menos, el precio no baja. La puede
   *  cambiar cualquier vendedor; el semáforo se mide igual. Sin modelo de esa marca para el servicio: va Growatt y se avisa. */
  marca_inversor_red: z.enum(MARCAS_INVERSOR_RED).nullable().optional(),
  iva_pct: z.union([z.literal(16), z.literal(8)]),
  ajustes: z.array(AjusteOpcion).max(3),
  formas_de_pago: FormasDePago,
  /** LEGADO (ver FinsusCopiado): desde A0 el motor no la usa. */
  finsus: z.array(FinsusCopiado).max(3),
  /** ADENDA A0b: tipo de cambio congelado del folio (lo pone parametrosIniciales con la tabla vigente). Ausente =
   *  borrador de antes: el motor usa el de la tabla vigente y lo avisa. */
  tipo_cambio: TipoCambioFolio.optional(),
  /** COSTOS-DIEGO (10-oct): comisión de ESTA cotización (% del contado sin IVA), si no es la del vendedor ("editable por
   *  cotización": Iván 10–12 % según el caso). Ausente/null = la del vendedor (EntradaMotor.comision_venta_pct). */
  comision_pct: z.number().min(0).max(50).nullable().optional(),
  /** Etapa 3 (Diego, 12-oct, punto 3): la generación por kW de ESTA cotización (factores, valor, motivo, a sabiendas) y el
   *  municipio de la obra si la ciudad no se reconoce. Ausente = la base de Diego (Ajustes) y el municipio de la ciudad. */
  generacion: GeneracionCotizacion.optional(),
  /** OPTIMUS (Diego, 12-oct noche, ADENDA c): el precio de Optimus CONGELADO de esta cotización (factor sobre la tabla y las
   *  comisiones con que se fijó). Lo pone el servidor al crearla si el vendedor es de Optimus, y SOLO lo cambia el recálculo
   *  explícito («Aplicar el precio de…», POST …/precio-optimus): una edición del borrador no lo toca (ni Diego). Ausente =
   *  venta normal. Lo ve solo Diego (vista.ts). */
  precio_optimus: PrecioOptimus.optional(),
  /** Promo de contado (Diego, 2-nov-2026): la pone o la quita el botón de la cotización y queda congelada aquí. Ausente o null = sin promo. */
  promo: PromoCotizacion.nullable().optional(),
  /** Baterías (Diego, 13-oct): la batería y el inversor híbrido que el VENDEDOR elige de la tabla (ids de
   *  TablaPrecios.baterias / inversores_hibridos). Ausente o null = los de omisión: el par compatible más barato para el
   *  cliente (Diego, 13-oct 23:00, respuesta u). Un id que la tabla ya no trae se avisa y se usa el de omisión. */
  equipo_respaldo: z
    .strictObject({
      bateria_id: z.string().regex(/^[a-z0-9_-]{2,40}$/).nullable(),
      inversor_id: z.string().regex(/^[a-z0-9_-]{2,40}$/).nullable(),
    })
    .nullable()
    .optional(),
  /** Diego, 14-oct-2026 22:30 (ADENDA «BATERÍAS EN OPCIONES Y RETORNO», MANDA): cuántas baterías (módulos) lleva cada nivel de
   *  respaldo, como lo ajustó el vendedor en Opciones; 0 = quitar ese nivel (siempre queda al menos uno); null = lo que
   *  calcula el motor para sus horas. Ausente o null = todos automáticos. Van con el par de `equipo_respaldo` (la pantalla lo
   *  fija al ajustar y los borra al cambiar de par). */
  modulos_respaldo: z
    .strictObject({ esencial: ModulosNivel, confort: ModulosNivel, completa: ModulosNivel })
    .nullable()
    .optional(),
  /** Parámetro de versión (ADENDA z48-1, Diego 28-oct-2026): en el HÍBRIDO por opción cada opción arranca con las baterías para 1 a
   *  2 horas (HORAS_RESPALDO_HIBRIDO), sin la escalera ~2/4/8 h ni la z28. Nace con true en los híbridos NUEVOS
   *  (parametrosIniciales) y al volver híbrida una de paneles (servidor); ausente = las de antes, que no cambian solas. */
  respaldo_1a2_horas: z.boolean().optional(),
  /** Parámetro de versión (Diego, 2-nov-2026; constantes.ts → FACTOR_ELECTRICA_HIBRIDO_AISLADO): híbrido o aislado con paneles: la parte
   *  eléctrica cuesta 2 veces la de un interconectado y lo de más entra al precio de la parte de baterías de cada nivel. Nace con true en
   *  las NUEVAS (parametrosIniciales) y al volverse híbrida o aislada una que no lo era (servidor); ausente = las de antes, que no cambian solas. */
  electrica_hibrido_aislado: z.boolean().optional(),
  /** VERDE DE ENTRADA (Diego, 2-nov-2026): el precio de entrada de cada opción, nivel y recorte es el mayor entre el de la tabla y el más bajo
   *  que queda en verde (motor/src/verde-de-entrada.ts). Nace con true en las NUEVAS que pasan por el motor solar; ausente = las de antes, que
   *  no cambian solas (y sin banda «Actualizar»: un aumento no se va colado). Lo cambia solo Diego. */
  verde_de_entrada: z.boolean().optional(),
  /** VERDE DE ENTRADA, decisión 7: lo que el mínimo subió cuando salió el PDF (contado CON IVA, por opción y paneles, o por nivel y equipo, o por
   *  gabinetes; `resultado.minimos_verde`). Con la clave aquí, la subida ya no se mueve sola: una propuesta enviada no sube porque suba un costo.
   *  Lo escribe solo el servidor al generar un PDF; no se edita a mano. Solo lo ve Diego (vista.ts). */
  verde_congelado: z.record(z.string().min(1).max(120), z.number().min(0)).optional(),
  /** ADENDA z48-10 (28-oct): cliente nuevo: el cuadro de cargas se reparte con la curva de Sonora (estimar-consumo.ts). Ausente = las de antes (dos bloques). */
  consumo_estacional: z.boolean().optional(),
  /** ADENDA z46-4 (P18, 28-oct): aislado chico: la Holgada lleva un panel más (7 → 8) para ir con el inversor de aislado más barato
   *  (SNA-US 6K) si aguanta sus equipos (motor.ts). Ausente = las de antes, que no cambian solas. */
  aislado_holgada_chico: z.boolean().optional(),
  /** ADENDA z46-8 (P20, Diego 27-oct-2026: «la generación BAJA SOLA con el sol de cada municipio»): en una obra cuyo municipio tiene
   *  claramente menos sol que Hermosillo (alerta de la investigación: Navojoa, Cajeme…) la generación de la cotización baja sola a la
   *  de su sol (base × factor del municipio), sin que el vendedor encienda nada; lo que dimensiona, el ahorro, el retorno y el PDF
   *  usan esa cifra. Nace con true en las cotizaciones NUEVAS (también la sucursal de un multisitio); ausente = las de antes, que no
   *  cambian solas (el sol sigue como sugerencia hasta «Actualizar a la versión nueva»). */
  sol_del_municipio: z.boolean().optional(),
  /** Parámetro de versión (ADENDA z53-4, Diego 28-oct-2026; listas de octubre): en baterías (respaldo, híbrido) y aislado, (1) el
   *  equipo por omisión ya NO es Growatt (malas experiencias; se puede elegir a mano en «equipo_respaldo») y (2) las baterías dan
   *  la POTENCIA de la casa (continua y de arranque, de su ficha) y el mínimo de Ah que pide el inversor (SNA-US 6000: 200 Ah).
   *  Nace con true en las NUEVAS (parametrosIniciales); ausente = las de antes, que no cambian solas (z45-2). */
  respaldo_oct26: z.boolean().optional(),
  /** ADENDA z46-7 (P19, Diego 27-oct-2026: «UN VIÁTICO POR CIUDAD (no uno por sucursal)») como PARÁMETRO DE VERSIÓN del
   *  multisitio (verificación de hoy68, 29-oct): las sucursales de una misma ciudad fuera de Hermosillo comparten UN viático
   *  (viaticosPorCiudad). Nace con true SOLO en las sucursales de un proyecto NUEVO (y en la que se agrega a un proyecto que ya lo
   *  trae en TODAS: servidor, viaticoPorCiudadDeLaNueva). Ausente = un proyecto guardado antes: sigue EXACTO como producción
   *  (viáticos en CADA sucursal fuera de Hermosillo; repetidos no detienen nada). El motor no lo lee: lo usa el servidor. */
  viatico_por_ciudad: z.boolean().optional(),
  /** ADENDA z67-1 (Diego, 30-oct-2026: «CADA PDF suelto lleva su viaje, por si el cliente firma ese sitio solo; el todo en uno lo
   *  cobra UNA vez por ciudad») como PARÁMETRO DE VERSIÓN del multisitio: con true, la sucursal fuera de Hermosillo lleva SUS
   *  viáticos en su cotización y en su PDF suelto (nunca «van en otra sucursal»), y el PDF de todas le quita el viaje a las demás
   *  de su ciudad (servidor: viaje-por-sitio.ts; PDF: mapearMultisitio). Nace con true en las sucursales de un proyecto NUEVO (y en
   *  la que se agrega a uno que ya lo trae en TODAS: viajePorSitioDeLaNueva). Ausente = un proyecto guardado antes: sigue EXACTO
   *  (un viático por ciudad en UNA sucursal, z46-7). El motor no lo lee: lo usa el servidor. */
  viaje_por_sitio: z.boolean().optional(),
  /** ADENDA z68-3 (Diego, 31-oct-2026: «todo en uno con casas (al bimestre) y negocios (al mes): dos columnas por sitio, la de su
   *  recibo y al año; el total del proyecto al año») como PARÁMETRO DE VERSIÓN del multisitio: con true en TODAS sus sucursales, el
   *  PDF de todas de un proyecto con una casa o un recibo bimestral sale en el formato «consolidado» (la casa y el bimestral ENTRAN);
   *  sin casas ni bimestrales sigue el formato multisitio de siempre. Nace con true en las sucursales de un proyecto NUEVO (y en la
   *  que se agrega a uno que ya lo trae en TODAS). Ausente = un proyecto guardado antes: su PDF de todas sigue EXACTO. */
  consolidado_v1: z.boolean().optional(),
  /** ADENDA z52-1 (Diego, 28-oct-2026): parámetro de versión. Un negocio (comercio o industria) con recibo de casa (1, 1A–1F o
   *  DAC) se calcula a futuro con la tarifa comercial PDBT (o GDBT si se escogió): dominio/tarifa-futura.ts → cambioDeTarifa.
   *  Nace con true en comercio e industria NUEVAS (parametrosIniciales); ausente = las de antes, que no cambian solas. */
  negocio_a_pdbt: z.boolean().optional(),
  /** Parámetro de versión (hoy99, Diego 2-nov-2026): hasta qué mes de 2026 cuenta esta cotización la tabla de CFE (los meses
   *  siguientes usan ese, como un mes sin publicar). Nace con CUOTAS_CFE_HASTA_MES en las NUEVAS; ausente = las de antes, con
   *  CUOTAS_CFE_SIN_PARAMETRO: no cambian solas al aplicar un mes nuevo, hasta «Actualizar a la versión nueva». */
  cuotas_cfe_hasta: z.int().min(1).max(12).optional(),
  /** ADENDA z48-2: las HORAS de respaldo que escribió el vendedor en cada opción (por su nivel, NIVEL_DE_OPCION); el motor pone la
   *  cantidad de baterías que deja las horas más cerca (nunca 0). Los módulos fijados (modulos_respaldo) mandan sobre estas: la
   *  pantalla borra una al escribir la otra. null = las automáticas. Solo híbrido (el aislado va por noches). */
  horas_respaldo: z
    .strictObject({ esencial: HorasNivel, confort: HorasNivel, completa: HorasNivel })
    .nullable()
    .optional(),
  /** Parámetro de versión (ADENDA z54/z55-2, 29-oct-2026): RECORTE DE PUNTA, método 1 (12 recibos GDMTH, tope 0.9 × punta, meses
   *  fallados 2 y 6, cuotas del último mes publicado todo el año). Nace con 1 en una industria NUEVA con cuestionario.recorte_punta
   *  (parametrosIniciales); un método nuevo sube a 2 con su fecha sin mover las de 1. Ausente = no se calcula recorte. */
  recorte_punta: z.literal(1).optional(),
  /** Recorte de punta: cuántos gabinetes escribió el vendedor (1 a 10). null/ausente = el motor elige el de mejor retorno (en
   *  empate, el de menos gabinetes). El motor lo respeta si pasa los candados y lo dice si no. */
  recorte_gabinetes: z.int().min(1).max(RECORTE_PUNTA_REGLAS.gabinetes_max).nullable().optional(),
});
export type ParametrosCalculo = z.infer<typeof ParametrosCalculo>;

// ───────────── Datos por periodo para comercial / industrial / horaria (nombres de ESPEC §3.3 y §3.5) ─────────────

/** "Tu recibo por renglón" (ESPEC 3.3), en pesos COMO LOS IMPRIME EL RECIBO (sin IVA). La energía baja con paneles; el
 *  cargo fijo, la capacidad y la distribución NO (candado N9 del generador en GDMTO/GDMTH). */
export const CargosPorRenglon = z.strictObject({
  fijo: Pesos,
  energia: Pesos,
  capacidad: Pesos,
  distribucion: Pesos,
});
export type CargosPorRenglon = z.infer<typeof CargosPorRenglon>;

/** kWh del periodo por horario de la tarifa GDMTH. */
export const KwhPorHorario = z.strictObject({
  base: z.number().nonnegative(),
  intermedia: z.number().nonnegative(),
  punta: z.number().nonnegative(),
});
export type KwhPorHorario = z.infer<typeof KwhPorHorario>;

/** Demanda del periodo en kW: en punta y máxima (la máxima nunca es menor que la de punta). */
/** Demanda medida del recibo (kW). `punta` null = la tarifa no la mide (GDMTO): CFE cobra capacidad y distribución contra la
 *  máxima (13-oct: antes GDMTO no traía demanda y la medición neta no se podía revisar en el PDF). */
export const DemandaKw = z.strictObject({ punta: z.number().nonnegative().nullable(), maxima: z.number().nonnegative() });
export type DemandaKw = z.infer<typeof DemandaKw>;

/** Cuotas de CFE del periodo (ESPEC 3.5, formato horaria): fijo en $ al mes; base, intermedia y punta en $/kWh;
 *  capacidad y distribución en $/kW. */
export const CuotasCfe = z.strictObject({
  fijo: Pesos,
  base: z.number().nonnegative(),
  intermedia: z.number().nonnegative(),
  punta: z.number().nonnegative(),
  capacidad: z.number().nonnegative(),
  distribucion: z.number().nonnegative(),
});
export type CuotasCfe = z.infer<typeof CuotasCfe>;

/** Un periodo ya consolidado (dos medidores sumados mes a mes; estimados marcados), en el orden en que va al PDF.
 *  Contrato v2: dias, kwh, demanda, cuotas y cargos son OPCIONALES (el motor los llena desde la lectura cuando el
 *  recibo los trae: GDMTO/GDMTH). Ausente = el motor todavía no los da; null = el recibo no los trae. */
export const PeriodoCalculo = z
  .strictObject({
    inicio: Fecha,
    fin: Fecha,
    etiqueta: z.string().min(1).max(40),
    consumo_kwh: z.number().nonnegative(),
    /** Lo que se paga hoy a CFE en ese periodo (del recibo; "pago hoy" sale del recibo, ESPEC 0.2). */
    pago_hoy: Pesos,
    origen: OrigenPeriodo,
    dias: z.int().min(1).max(70).nullable().optional(),
    kwh: KwhPorHorario.nullable().optional(),
    /** Tanda 2 (verificación del 25-oct): `kwh` lo PROYECTÓ LA IA con el patrón de los otros recibos (no lo imprimió CFE).
     *  El motor lo usa para el ahorro y para estimar el pago; el PDF lo trata como faltante (nunca va como dato de CFE).
     *  Ausente = el reparto es del recibo (o no hay). Opcional: las cotizaciones guardadas antes siguen valiendo. */
    kwh_proyectado_ia: z.literal(true).optional(),
    /** 4b del barrido (25-oct; D5 de Diego, ADENDA z28-14 y z29): el consumo es real (recibo o su historial) pero lo que se
     *  PAGÓ ese periodo no viene en el recibo (con 1 o 2 recibos y su historial): el motor lo estimó con las cuotas de CFE. El
     *  PDF ya no dice «12 recibos» ni «copiados» y marca esos meses «Estimado». Solo en periodos con consumo real (los de origen
     *  «estimado» ya van como estimados). Ausente = el pago salió del recibo (o es una cotización guardada antes). */
    pago_estimado: z.literal(true).optional(),
    /** ADENDA z52 (base futura): lo que dice el RECIBO de ese periodo (kWh y pago, en su tarifa de hoy) cuando el cálculo usa
     *  otra base (cambio de tarifa de un negocio con recibo de casa y/o aumento de consumo). `consumo_kwh` y `pago_hoy` son
     *  entonces los de cálculo (con el aumento; en la tarifa futura). Ausente = el cálculo es el recibo (como siempre). */
    kwh_recibo: z.number().nonnegative().optional(),
    pago_recibo: Pesos.optional(),
    /** ADENDA z52-2: kWh DE MÁS que se sumaron a este periodo por el aumento de consumo (≥ 0). Ausente = sin aumento. */
    aumento_kwh: z.number().nonnegative().optional(),
    /** En el JSON v1 se llama `demanda_kw` (ESPEC 3.3); aquí `demanda` porque el motor ya usa `demanda_kw` (un número:
     *  la demanda facturable) en su tipo interno que extiende a este. El puente la copia como demanda_kw. */
    demanda: DemandaKw.nullable().optional(),
    /** 14-oct: la demanda con que el motor aplica la fórmula de CFE cuando el recibo NO la trae (la cobrada o la de la
     *  fórmula; ver motor.ts, solo_si_cero). No es «leída»: `demanda` sigue null. El puente la manda al PDF como demanda_kw. */
    demanda_supuesta: DemandaKw.optional(),
    cuotas: CuotasCfe.nullable().optional(),
    cargos: CargosPorRenglon.nullable().optional(),
  })
  .superRefine((p, ctx) => {
    if (p.kwh) {
      const suma = p.kwh.base + p.kwh.intermedia + p.kwh.punta;
      if (Math.abs(suma - p.consumo_kwh) > Math.max(1, p.consumo_kwh * 0.01))
        ctx.addIssue({
          code: 'custom',
          path: ['kwh'],
          message: `Periodo ${p.etiqueta}: base + intermedia + punta = ${suma} kWh y el consumo dice ${p.consumo_kwh} kWh (¿multiplicador del medidor o recibo de otro mes?).`,
        });
    }
    if (p.demanda && p.demanda.punta !== null && p.demanda.maxima < p.demanda.punta)
      ctx.addIssue({ code: 'custom', path: ['demanda'], message: `Periodo ${p.etiqueta}: la demanda máxima (${p.demanda.maxima} kW) no puede ser menor que la de punta (${p.demanda.punta} kW).` });
  });
export type PeriodoCalculo = z.infer<typeof PeriodoCalculo>;

export const LineaMonto = z.strictObject({ concepto: Texto(2, 160), monto_sin_iva: z.number(), confirmado: z.boolean() });
export type LineaMonto = z.infer<typeof LineaMonto>;

export const DesglosePrecio = z.strictObject({
  precio_lista_sin_iva_por_w: z.number().positive(),
  equipo_e_instalacion_sin_iva: Pesos,
  fijos: z.array(LineaMonto),
  adicionales: z.array(LineaMonto),
  ajuste_manual: z.strictObject({ monto_sin_iva: z.number(), motivo: Texto(3, 300) }).nullable(),
  lista_sin_iva: Pesos,
  descuento_contado_pct: z.number().min(0).max(30),
  contado_sin_iva: Pesos,
  iva_pct: z.union([z.literal(16), z.literal(8)]),
  /** Con IVA (las cifras que van al PDF). */
  lista: Pesos,
  contado: Pesos,
  /** ADENDA A0b (11-oct, "en cuántos centavos por watt estamos cotizando", lo ven todos): el contado SIN IVA de la opción
   *  (todo: equipo, fijos, adicionales, ajuste y redondeo) entre sus watts, en pesos y en dólares (con el TC del folio). */
  contado_sin_iva_por_w: z.number().positive().optional(),
  contado_sin_iva_usd_por_w: z.number().positive().optional(),
  /** ADENDA A0b · tabla en dólares: el contado por watt del RANGO de la tabla (USD/W) y el tipo de cambio del folio con
   *  que se convirtió. Ausente = tabla en pesos. */
  contado_usd_por_w_tabla: z.number().positive().optional(),
  tipo_cambio: z.number().positive().optional(),
  /** ADENDA A0b (11-oct): lo que movió el redondeo del contado con IVA a $100, en el contado SIN IVA (puede ser
   *  negativo). Con él: contado = redondeado, lista = contado ÷ 0.85. Ausente = sin redondeo (motor de antes). */
  redondeo_contado_sin_iva: z.number().optional(),
  /** ADENDA A0b: cuánto % quedó la lista ABAJO de la de la tabla (negativo = arriba). Más que el tope sin aprobación
   *  pide el OK de Diego (aviso DESCUENTO_SIN_APROBAR). Ausente = sin ajuste manual. */
  descuento_vs_tabla_pct: z.number().optional(),
  /** PRECIO ÚNICO (Diego, 13-oct-2026, ADENDA punto 5): comercio o industria con el contado CON IVA de la tabla arriba del
   *  umbral (umbrales.precio_unico_arriba_de_con_iva, $1,000,000). Un solo precio, el de contado: lista = contado,
   *  descuento_contado_pct = 0, sin MSI; los renglones (equipo, fijos, adicionales, precio por W y el ajuste a mano) van
   *  en ese mismo precio, y el ajuste del vendedor (precio_lista_sin_iva del ajuste) ES el precio único sin IVA. Ausente =
   *  lista y contado como siempre. */
  precio_unico: z.literal(true).optional(),
  /** Promo de contado: `contado` ya es el precio de promo; aquí queda el contado normal, base de etapas, FINSUS y arrendamiento. */
  promo: z.strictObject({ pct: z.number(), contado_normal: Pesos, contado_normal_sin_iva: Pesos }).optional(),
});
export type DesglosePrecio = z.infer<typeof DesglosePrecio>;

export const Semaforo = z.enum(['verde', 'amarillo', 'rojo']);
export type Semaforo = z.infer<typeof Semaforo>;

export const Rentabilidad = z.strictObject({
  costo_total_sin_iva: Pesos,
  desglose_costo: z.array(LineaMonto),
  utilidad_contado_sin_iva: z.number(),
  margen_contado_pct: z.number(),
  semaforo: Semaforo,
  motivos: z.array(z.string().min(3).max(300)),
  usa_costos_sin_confirmar: z.boolean(),
  /** Diego, 13-oct (pedido 6): si el cliente paga en EFECTIVO con el descuento máximo (contadoEnEfectivo: 5 % del total con
   *  IVA; el IVA no se quita, así que el descuento sale completo de lo que le queda a Solarix; costos y comisión iguales).
   *  Solo Diego (va dentro de la rentabilidad: vista.ts la quita entera a los demás). Ausente = motor de antes. */
  efectivo: z
    .strictObject({
      /** Lo que paga el cliente en efectivo, con IVA (redondeado a $100). */
      contado: Pesos,
      /** contado con IVA − efectivo. */
      descuento: Pesos,
      utilidad_sin_iva: z.number(),
      margen_pct: z.number(),
    })
    .optional(),
  /** COSTOS-DIEGO: la comisión que se restó (la del vendedor o la de la cotización). Ausente = motor de antes. */
  comision_pct: z.number().min(0).max(50).optional(),
  /** Con qué se costeó: "por_tamano" (COSTOS-DIEGO, el inversor de la lista del proveedor) o "costo_unico" (una sola
   *  cifra por watt para todos los tamaños). Ausente = motor de antes. */
  costeo: z.enum(['por_tamano', 'costo_unico']).optional(),
  /** OPTIMUS (Diego, 12-oct noche): quién cobra qué y con qué margen se juzgó el semáforo. Ausente = venta normal. */
  optimus: z
    .strictObject({
      /** La del vendedor (de Optimus) y la de Juan Carlos Cortés (aliado); comision_pct es la suma. */
      comision_vendedor_pct: z.number().min(0).max(50),
      comision_aliado_pct: z.number().min(0).max(50),
      /** true = el precio de la cotización lleva el factor de Optimus. */
      precio_optimus: z.boolean(),
      /** Con el precio de Optimus: el margen de una venta normal (6 %) que dejaría la misma utilidad; el semáforo lo usa.
       *  null = el precio no lleva el factor (el semáforo usa el margen real). */
      margen_equivalente_pct: z.number().nullable(),
    })
    .optional(),
});
export type Rentabilidad = z.infer<typeof Rentabilidad>;

/** La rentabilidad COMPLETA de una opción o nivel (motor, IA y servidor la tienen siempre). Truena si falta: una
 *  rentabilidad oculta (vista para quien no ve costos) nunca debe llegar a quien decide con ella. */
export function rentabilidadDe(o: { rentabilidad?: Rentabilidad | null | undefined }, quien = 'la opción'): Rentabilidad {
  if (!o.rentabilidad) throw new Error(`Falta la rentabilidad de ${quien}: se recibió una vista sin costos donde hacía falta la completa.`);
  return o.rentabilidad;
}

// ───────────── FINSUS de REFERENCIA (ADENDA A0, 10-oct-2026) ─────────────

/** Mensualidad de REFERENCIA de FINSUS, calculada SIEMPRE por el motor (para cualquier monto, precotización o final)
 *  con la fórmula y la tasa que reproducen al peso las 6 cotizaciones reales de FINSUS (paquetes/motor/datos/finsus.json,
 *  copia fiel de financieras.json de la plantilla). NO es una cotización de FINSUS ni "crédito bancario".
 *  Lo que se enseña: "Enganche $X (10 %) · desde $Y al mes a 60 meses" + la TASA anual; el CAT va SOLO en la leyenda
 *  chiquita (textoLeyendaFinsus). Nunca levanta un aviso rojo (ver problemasFinsusA0). */
export const FinsusReferencia = z.strictObject({
  /** Contado con IVA de la opción o nivel (la P de la fórmula). */
  precio_base: Pesos,
  enganche_pct: z.literal(FINSUS_REFERENCIA.enganche_pct),
  enganche: Pesos,
  /** Lo financiado: (1 − enganche) · P + apertura con IVA + seguros (fórmula de financieras.json). */
  financiado: z.number().positive(),
  plazo_meses: z.literal(FINSUS_REFERENCIA.plazo_meses),
  /** REDONDEO_ARRIBA(PMT(tasa · (1 + IVA) / 12, plazo, financiado)). */
  mensualidad: z.number().positive(),
  /** Tasa de interés anual fija SIN IVA (hoy 16 %): ES LO QUE SE IMPRIME. La mensualidad le suma el IVA de los
   *  intereses (iva_intereses_pct). */
  tasa_anual_pct: z.number().positive().max(80),
  iva_intereses_pct: z.number().min(0).max(30),
  /** El CAT sin IVA que imprime la leyenda chica, informativo (nunca protagonista, nunca en la franja). OJO, pese al
   *  nombre NO es un promedio: es el mismo cálculo que la plantilla A0 (catParaLeyenda) — por cada opción, el CAT de la
   *  cotización FINSUS de referencia VIGENTE (≤ 6 meses) más cercana en monto; de todas, el MÁS ALTO (no subestima el
   *  costo de ninguna), y ese mismo valor va en todas las opciones de la hoja. Se llama "promedio" porque la leyenda de
   *  la plantilla dice "CAT promedio" (decisión de texto pendiente de Diego). null = no hay referencias vigentes o Diego
   *  quitó el CAT (la leyenda sale sin CAT). */
  cat_promedio_pct: z.number().positive().max(80).nullable(),
  /** Fecha en que FINSUS calculó ese CAT (la de su cotización de referencia): la leyenda de la plantilla la imprime
   *  ("informativo (24/09/2026, sobre $223,285)"). Con cat_sobre_precio: los dos o ninguno; null si no hay CAT.
   *  Ausente = motor de antes de que existiera. */
  cat_fecha: Fecha.nullable().optional(),
  /** Precio sobre el que FINSUS calculó ese CAT (el contado de su cotización de referencia), también en la leyenda. */
  cat_sobre_precio: Pesos.nullable().optional(),
  /** ¿El contado cae dentro del rango de montos de las cotizaciones de referencia? Informativo: fuera se calcula igual. */
  dentro_de_referencias: z.boolean(),
  /** financieras.json → medido_el. */
  medido_el: Fecha,
  fuente: Texto(3, 300),
  /** Producto de FINSUS (16-oct-2026; productoFinsus del cuestionario): «persona» (la fórmula de siempre) o «empresarial»
   *  (persona moral o PF con actividad empresarial: tasa sin IVA en intereses, sin seguro de vida, apertura de contado, SIN CAT). Ausente = motor de antes
   *  (persona). */
  producto: z.enum(['persona', 'empresarial']).optional(),
  /** Comisión por apertura PAGADA DE CONTADO. «empresarial»: 2 % · financiado · 1.16 (desde el 16-oct). «persona»: desde el
   *  28-oct-2026 (Diego) también de contado, 2 % · (contado − enganche) · 1.16; null/ausente = motor de antes (financiada). */
  apertura_contado: Pesos.nullable().optional(),
  /** Seguro de daños PAGADO DE CONTADO (Diego, 28-oct-2026, los dos productos): (300 + 2.5 % · P) · 1.16. null/ausente =
   *  motor de antes (financiado dentro de la mensualidad). Lo que se paga al inicio = enganche + este + apertura_contado. */
  seguro_danos_contado: Pesos.nullable().optional(),
});
export type FinsusReferencia = z.infer<typeof FinsusReferencia>;

const fmtPct = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 2 });

const fechaCorta = (iso: string) => iso.split('-').reverse().join('/');

/** La leyenda chiquita de A0 con la tasa y el CAT (sin CAT si no hay referencias). Si el motor dice de qué cotización
 *  sale el CAT (cat_fecha y cat_sobre_precio), lo agrega como la plantilla: "informativo (24/09/2026, sobre
 *  $223,285)". La usan la interfaz y, como referencia de texto, el puente del PDF. */
export function textoLeyendaFinsus(f: Pick<FinsusReferencia, 'tasa_anual_pct' | 'cat_promedio_pct'> & Partial<Pick<FinsusReferencia, 'cat_fecha' | 'cat_sobre_precio' | 'producto' | 'seguro_danos_contado'>>): string {
  // 28-oct-2026 (Diego): seguro de daños y apertura de contado → la leyenda lo dice (y el CAT, solo si lo hay)
  if (typeof f.seguro_danos_contado === 'number') {
    const base = (f.producto === 'empresarial' ? FINSUS_REFERENCIA.leyenda_empresarial_contado : FINSUS_REFERENCIA.leyenda_contado).replace('{tasa}', fmtPct.format(f.tasa_anual_pct));
    return f.cat_promedio_pct === null ? base : base.replace('. Sujeto a', `; CAT promedio ${fmtPct.format(f.cat_promedio_pct)} % sin IVA. Sujeto a`);
  }
  if (f.producto === 'empresarial') return FINSUS_REFERENCIA.leyenda_empresarial.replace('{tasa}', fmtPct.format(f.tasa_anual_pct));
  const base = FINSUS_REFERENCIA.leyenda.replace('{tasa}', fmtPct.format(f.tasa_anual_pct));
  if (f.cat_promedio_pct === null) return base.replace(/, CAT promedio \{cat\} % sin IVA/, '');
  const conCat = base.replace('{cat}', fmtPct.format(f.cat_promedio_pct));
  return f.cat_fecha && typeof f.cat_sobre_precio === 'number'
    ? conCat.replace('informativo.', `informativo (${fechaCorta(f.cat_fecha)}, sobre ${pesos(f.cat_sobre_precio, { enteros: true })}).`)
    : conCat;
}

/** Problemas de un FINSUS de referencia contra el contado (con IVA) de su opción. Vacío = cuadra. */
export function problemasFinsusReferencia(f: FinsusReferencia, contado: number, quien: string): string[] {
  const p: string[] = [];
  if (Math.abs(f.precio_base - contado) > 1) p.push(`FINSUS de ${quien}: el precio base (${f.precio_base}) no es el contado con IVA (${contado}).`);
  const eng = (f.precio_base * f.enganche_pct) / 100;
  if (Math.abs(f.enganche - eng) > 1) p.push(`FINSUS de ${quien}: el enganche (${f.enganche}) no es el ${f.enganche_pct} % del precio base (${eng.toFixed(2)}).`);
  // 28-oct-2026: con el seguro y la apertura de contado, lo financiado puede ser EXACTAMENTE precio − enganche (empresarial)
  const contadoNuevo = typeof f.seguro_danos_contado === 'number';
  if (contadoNuevo ? f.financiado < f.precio_base - f.enganche - 0.01 : f.financiado <= f.precio_base - f.enganche)
    p.push(`FINSUS de ${quien}: lo financiado (${f.financiado}) ${contadoNuevo ? 'no puede ser menor' : 'debe incluir apertura y seguros: no puede ser menor'} que precio − enganche.`);
  const total = f.mensualidad * f.plazo_meses;
  if (total <= f.financiado) p.push(`FINSUS de ${quien}: ${f.plazo_meses} mensualidades de ${f.mensualidad} no pagan ni lo financiado (${f.financiado}): ¿falta la tasa?`);
  if (total > f.financiado * 3) p.push(`FINSUS de ${quien}: ${f.plazo_meses} mensualidades de ${f.mensualidad} son más del triple de lo financiado: ¿un punto decimal corrido?`);
  if (f.cat_promedio_pct !== null && f.cat_promedio_pct <= f.tasa_anual_pct)
    p.push(`FINSUS de ${quien}: el CAT (${f.cat_promedio_pct} %) debe ser mayor que la tasa (${f.tasa_anual_pct} %): ¿se cruzaron?`);
  // De qué cotización sale el CAT: fecha y precio van juntos, y solo si hay CAT.
  const fecha = f.cat_fecha ?? null;
  const sobre = f.cat_sobre_precio ?? null;
  if ((fecha === null) !== (sobre === null)) p.push(`FINSUS de ${quien}: la fecha y el precio del CAT van los dos o ninguno.`);
  if (f.cat_promedio_pct === null && (fecha !== null || sobre !== null)) p.push(`FINSUS de ${quien}: sin CAT no puede traer fecha ni precio del CAT.`);
  // Producto «empresarial» (persona moral, 16-oct-2026): sin CAT, sin IVA en intereses y con la apertura de contado.
  const apertura = f.apertura_contado ?? null;
  if (f.producto === 'empresarial') {
    if (f.cat_promedio_pct !== null) p.push(`FINSUS de ${quien}: el crédito empresarial no lleva CAT (no hay CAT de referencia de ese producto).`);
    if (f.iva_intereses_pct !== 0) p.push(`FINSUS de ${quien}: el crédito empresarial no cobra IVA en intereses (vino ${f.iva_intereses_pct} %).`);
    if (apertura === null || !(apertura > 0)) p.push(`FINSUS de ${quien}: el crédito empresarial paga la comisión por apertura de contado y no la trae.`);
  } else if (apertura !== null && !contadoNuevo) p.push(`FINSUS de ${quien}: la comisión por apertura de contado es solo del crédito empresarial (persona moral), salvo con el seguro de daños de contado (28-oct-2026).`);
  // 28-oct-2026 (Diego): el seguro de daños de contado va SIEMPRE con la apertura de contado, los dos mayores que 0
  if (contadoNuevo && (!(f.seguro_danos_contado! > 0) || apertura === null || !(apertura > 0)))
    p.push(`FINSUS de ${quien}: con el seguro de daños de contado (28-oct-2026) también va la comisión por apertura de contado, los dos mayores que 0.`);
  return p;
}

// ───────────── Opciones solares ─────────────

export const OpcionCalculada = z.strictObject({
  id: OpcionId,
  cobertura_objetivo: z.number().positive(),
  /** Σ generación ÷ Σ consumo. */
  cobertura_real: z.number().nonnegative(),
  paneles: z.int().min(1),
  panel: Panel,
  kwp: z.number().positive(),
  inversores: z.array(InversorElegido).min(1),
  /** Uno por periodo, en el orden de ResultadoMotor.periodos. */
  generacion_kwh: z.array(z.number().nonnegative()),
  pago_con_sistema: z.array(Pesos),
  queda_en_minimo: z.boolean(),
  ahorro_anual: z.number(),
  /** Diego, 13-oct (pedido 2): % del recibo de hoy que se ahorra (ahorro_anual ÷ Σ pago_hoy × 100, un decimal; en GDMTO/GDMTH
   *  incluye la baja de demanda). Con él se buscan los paneles de un `AjusteOpcion.ahorro_pct`. Ausente = motor de antes. */
  ahorro_pct: z.number().optional(),
  /** Diego, 12-oct noche (punto a): en GDMTO/GDMTH, la parte de `ahorro_anual` (con IVA, año típico) que viene de la baja de
   *  CAPACIDAD y DISTRIBUCIÓN con la fórmula de CFE sobre los kWh netos facturados (el resto es energía). 0 = la regla no
   *  aplicó (falta la demanda medida de algún mes, u otra tarifa). Ausente = motor de antes. */
  ahorro_demanda: Pesos.optional(),
  /** Con `ResultadoMotor.demanda_cfe`: los kWh que CFE FACTURARÍA cada periodo con el sistema (medición neta con bolsa de 12
   *  meses, año típico), en el orden de `periodos`. La plantilla los usa para revisar con la fórmula de CFE que el pago con
   *  sistema no baje de lo que CFE cobra de capacidad y distribución (N9 con medición neta). Ausente = no aplica. */
  kwh_facturados: z.array(z.number().nonnegative()).optional(),
  /** Diego, 12-oct: años para recuperar el CONTADO con la luz subiendo (6 %/año desde el 12-oct noche) y los paneles
   *  perdiendo 0.42 %/año (aniosRetorno, un decimal; la plantilla hace la misma cuenta). null si no hay ahorro. */
  retorno_anios: z.number().positive().nullable(),
  /** Diego, 28-oct-2026: solo AISLADO. Lo que costaría generar con un generador de diésel la energía de sus equipos
   *  (motor/datos/generador-diesel.json, con fuente); su `retorno_anios` es el del SISTEMA completo contra este costo. */
  generador: z
    .strictObject({ kwh_dia: z.number().nonnegative(), kw_nominal: z.number().positive(), litros_dia: z.number().nonnegative(), litros_por_kwh: z.number().nonnegative(), precio_litro: z.number().positive(), costo_mes: Pesos, costo_anual: Pesos })
    .optional(),
  /** Diego, 12-oct: incentivo fiscal (solo si el cliente deduce: deduceImpuestos). Todo SIN IVA: beneficio = contado sin IVA ×
   *  tasa de ISR; retorno con (contado sin IVA − beneficio) y el ahorro sin IVA, con la luz subiendo. Ausente/null = no deduce. */
  fiscal: z.strictObject({ tasa_isr_pct: z.number().positive(), beneficio: Pesos, retorno_anios: z.number().positive().nullable() }).nullable().optional(),
  precio: DesglosePrecio,
  /** El motor SIEMPRE la calcula. AUSENTE = la persona que pidió la cotización no ve costos ni margen (ADENDA A0b, 11-oct:
   *  "solo yo ver márgenes"): el servidor la quita de la respuesta (vista.ts). Quien la necesite completa (motor, IA,
   *  servidor) usa rentabilidadDe(), que truena si falta. */
  rentabilidad: Rentabilidad.optional(),
  /** SOLO en la vista de un asesor interno (Diego, 11-oct: "que lo vean Miguel y Juan Carlos pero sin porcentajes, solo el
   *  color"): el color del semáforo de `rentabilidad`, sin nada más. Lo pone vista.ts al quitar la rentabilidad; el motor
   *  nunca lo escribe, el director no lo recibe (tiene la rentabilidad completa) y un externo tampoco. */
  semaforo_margen: Semaforo.optional(),
  /** Diego, 13-oct (junta: «que jueguen con el precio para que salga verde o amarillo; un botón para hacerlo verde»). Todo en
   *  CONTADO CON IVA (lo que ve el cliente y teclea el vendedor, múltiplos de $100): el de la TABLA (sin ajuste a mano) y el
   *  más bajo que queda en verde y en amarillo (null = ningún precio llega a ese color). Son precios, no costos: los ve quien
   *  ve el color del semáforo (vista.ts). `descuento_contado_pct` (el de la tabla, aunque la opción sea de precio único) es
   *  para convertir el contado tecleado al campo que guarda el motor (listaDesdeContado). Ausente = motor de antes. */
  limites_precio: z
    .strictObject({
      contado_tabla: Pesos,
      descuento_contado_pct: z.number().min(0).max(100),
      verde: Pesos.nullable(),
      amarillo: Pesos.nullable(),
      /** VERDE DE ENTRADA: lo que daría la tabla sin la regla (`contado_tabla` ya es el precio de entrada). Solo lo ve Diego (vista.ts). */
      contado_sin_regla: Pesos.optional(),
    })
    .optional(),
  /** ADENDA A0 (contrato v2). Ausente = motor de antes de A0; null = FINSUS apagado en formas_de_pago; objeto =
   *  calculado. Con A0 TODAS las opciones traen la clave (ver problemasFinsusA0). */
  finsus_referencia: FinsusReferencia.nullable().optional(),
  /** Diego, 14-oct (z16) y 20-oct (z23): en casa 1F la primera opción (id «arranque») es «Mejor retorno»: los paneles con el
   *  menor retorno, solo lo más caro del recibo. Ausente = el Arranque de cobertura fija de siempre. */
  mejor_retorno: z.literal(true).optional(),
  /** z16/z23 (20-oct, regla de fondo): los paneles de esta opción salieron de la búsqueda por retorno de casa 1F (o de un
   *  ajuste con `paneles_de_retorno`), lleve o no el nombre «Mejor retorno» (el candado de precios finales se lo puede quitar).
   *  Con esta marca, escribir solo el precio de la opción guarda ESTOS paneles (lo que el vendedor ve es lo que se vende). */
  paneles_por_retorno: z.literal(true).optional(),
  /** ADENDA z24 (20-oct): la primera opción lleva los inversores de la Exacta (interruptor en Opciones): quedan listos para
   *  ampliar hasta `paneles_hasta` paneles (los de la Exacta; en una ampliación, z34-6, el TOTAL: los que ya tiene + los de la Exacta), con los MISMOS paneles que tendría apagada. `mismo_inversor`: ya
   *  llevaba ese inversor (el precio no cambia). `precio_escrito`: el vendedor escribió el precio y ese se imprime (no incluye
   *  la diferencia del inversor). Ausente = su inversor justo, como siempre. */
  inversor_listo_para_ampliar: z
    .strictObject({ paneles_hasta: z.int().min(1).max(20_000), mismo_inversor: z.literal(true).optional(), precio_escrito: z.literal(true).optional() })
    .optional(),
});
export type OpcionCalculada = z.infer<typeof OpcionCalculada>;

// ───────────── Respaldo con baterías (contrato v2; ESPEC 3.4) ─────────────

export const BateriaElegida = z.strictObject({
  /** id en TablaPrecios.baterias; null = la batería no está en la tabla (el nivel sale sin precio). */
  id_tabla: z.string().nullable(),
  marca: Texto(2, 40),
  modelo: Texto(2, 60),
  quimica: Texto(2, 20),
  modulos: z.int().min(1).max(64),
  kwh_nominales: z.number().positive(),
  kwh_utiles: z.number().positive(),
  garantia_anios: z.int().min(1).max(30),
});
export type BateriaElegida = z.infer<typeof BateriaElegida>;

export const InversorRespaldo = z.strictObject({
  /** id en TablaPrecios.inversores_hibridos; null = no está en la tabla (el nivel sale sin precio). */
  id_tabla: z.string().nullable(),
  marca: Texto(2, 40),
  modelo: Texto(2, 60),
  kw_continuos: z.number().positive(),
  ms_cambio: z.number().nonnegative().max(1_000).nullable(),
  cantidad: z.int().min(1).max(20),
});
export type InversorRespaldo = z.infer<typeof InversorRespaldo>;

/** Precio de un nivel de respaldo: subtotal sin IVA + IVA + total, como todo (adenda A3). */
export const PrecioRespaldo = z.strictObject({
  /** Baterías, inversor, instalación / tablero de respaldo (costos fijos), adicionales. */
  lineas: z.array(LineaMonto).min(1),
  lista_sin_iva: Pesos,
  descuento_contado_pct: z.number().min(0).max(30),
  contado_sin_iva: Pesos,
  iva_pct: z.union([z.literal(16), z.literal(8)]),
  lista: Pesos,
  contado: Pesos,
  /** ADENDA A0b (11-oct): lo que movió el redondeo del contado con IVA a $100, en el contado sin IVA. */
  redondeo_contado_sin_iva: z.number().optional(),
});
export type PrecioRespaldo = z.infer<typeof PrecioRespaldo>;

export const NivelRespaldoCalculado = z
  .strictObject({
    id: NivelRespaldo,
    /** Vacía en el respaldo de TODA la casa (11-oct): no hay cargas críticas; solo el aislado lleva sus equipos. */
    cargas: z.array(CargaRespaldo).max(40),
    /** Lo que se tendrá conectado al respaldo, en kW (de menos a más uso). */
    carga_kw: z.strictObject({ min: z.number().positive(), max: z.number().positive() }),
    bateria: BateriaElegida,
    inversor: InversorRespaldo.nullable(),
    /** floor(kwh_utiles ÷ carga_kw.max) a floor(kwh_utiles ÷ carga_kw.min) (ESPEC 3.4): nunca una sola cifra. */
    horas: z.strictObject({ min: z.number().nonnegative(), max: z.number().nonnegative() }),
    /** null = la tabla no trae precio de esa batería o de ese inversor: aviso RESPALDO_SIN_PRECIO. NUNCA un precio inventado. */
    precio: PrecioRespaldo.nullable(),
    /** null = sin precio. AUSENTE = oculta para quien no ve costos (vista.ts), como en OpcionCalculada. */
    rentabilidad: Rentabilidad.nullable().optional(),
    /** Como en OpcionCalculada: SOLO en la vista de un asesor interno, el color del semáforo (vista.ts). */
    semaforo_margen: Semaforo.optional(),
    /** ADENDA A0 también aquí (null = FINSUS apagado o nivel sin precio). */
    finsus_referencia: FinsusReferencia.nullable(),
    /** Híbrido y aislado con paneles (Diego, 14-oct-2026 22:30, MANDA): lo que cuesta el RESPALDO sin los paneles (baterías +
     *  inversor híbrido + accesorios de respaldo), con la fórmula de equipos sueltos. Es seguridad, no ahorro: NO tiene
     *  retorno (el retorno es solo de los paneles, el de su opción). `precio` del nivel = paneles + esto. Su rentabilidad la
     *  ve SOLO Diego (vista.ts la quita como cualquier `rentabilidad`). Ausente = respaldo sin paneles (el `precio` del nivel
     *  ya es el respaldo) o motor de antes. */
    solo_respaldo: z
      .strictObject({
        precio: PrecioRespaldo,
        rentabilidad: Rentabilidad.nullable().optional(),
        semaforo_margen: Semaforo.optional(),
      })
      .optional(),
    /** Baterías POR OPCIÓN (Diego, 24-oct-2026; DISENO-BATERIAS-POR-OPCION): la opción a la que va ligado este nivel, uno a
     *  uno con NIVEL_DE_OPCION (esencial = Arranque, confort = Exacta, completa = Holgada). También en solo respaldo («Hasta
     *  N horas», sin paneles). Ausente = resultado de antes (ResultadoRespaldo sin `por_opcion`). */
    opcion: OpcionId.optional(),
    /** Las horas de toda la casa con que se dimensionó (por omisión HORAS_OBJETIVO_DE_OPCION: ~2/4/8). Las que da de verdad
     *  con las baterías que quedaron van en `horas` (rango). */
    horas_objetivo: z.number().positive().max(168).optional(),
    /** AISLADO por opción (E7, decisión 7): las noches de autonomía con que se dimensionó su banco (NOCHES_DE_OPCION: 1, 1.5
     *  o 2). El aislado no va por horas de toda la casa: con `noches` no trae `horas_objetivo`. */
    noches: z.number().positive().max(7).optional(),
    /** Aislado (Diego, 28-oct: «+/− baterías»): las noches que aguantan de verdad las baterías que quedaron (kWh útiles ÷ lo que
     *  se usa de noche × (1 + reserva)), a 0.1 hacia abajo. Sube y baja con los módulos; `noches` es el objetivo. Ausente = de
     *  antes o sin uso de noche. Solo con `noches`. */
    noches_cubre: z.number().min(0).max(999).optional(),
    /** Con paneles nuevos (híbrido o aislado) y `por_opcion`: el SISTEMA de este nivel = los paneles de SU opción + este
     *  respaldo. `precio` del nivel = `paneles_contado` + `solo_respaldo.precio.contado` (el esquema lo exige) y los paneles
     *  son los de su OpcionCalculada (ResultadoMotor lo exige). El retorno sigue saliendo solo de los paneles (ADENDA 14-oct).
     *  Es `ResultadoRespaldo.hibrido` nivel por nivel; con `por_opcion`, `hibrido` queda como el sistema del nivel recomendado
     *  (para lo que todavía lee uno solo). Sin precio de paneles: es precio, no costo, lo ven todos. */
    sistema: z
      .strictObject({
        opcion: OpcionId,
        /** Contado CON IVA de los paneles de esa opción (el MISMO de su OpcionCalculada.precio.contado). */
        paneles_contado: Pesos,
        /** Como en `hibrido.acople`: 'ca' = inversor de red + híbrido acoplado en CA; 'cd' = los paneles entran directo al
         *  (a los) híbrido(s) y el inversor de red de la opción sale. */
        acople: z.enum(['ca', 'cd']),
        /** Solo con 'cd': el inversor de red de la opción que sale («1 × Growatt MIN 8000TL-X2»). */
        inversor_red: z.string().min(3).max(120).optional(),
      })
      .optional(),
  })
  .superRefine((n, ctx) => {
    if (n.opcion !== undefined && NIVEL_DE_OPCION[n.opcion] !== n.id)
      ctx.addIssue({ code: 'custom', path: ['opcion'], message: `Nivel ${n.id}: va con la opción ${OPCION_DE_NIVEL[n.id]}, no con ${n.opcion} (NIVEL_DE_OPCION).` });
    if (n.sistema) {
      if (n.opcion === undefined) ctx.addIssue({ code: 'custom', path: ['opcion'], message: `Nivel ${n.id}: trae sistema y no dice de qué opción es.` });
      else if (n.sistema.opcion !== n.opcion)
        ctx.addIssue({ code: 'custom', path: ['sistema', 'opcion'], message: `Nivel ${n.id}: el sistema es de la opción ${n.sistema.opcion} y el nivel de ${n.opcion}.` });
      if (n.sistema.inversor_red !== undefined && n.sistema.acople !== 'cd')
        ctx.addIssue({ code: 'custom', path: ['sistema', 'inversor_red'], message: `Nivel ${n.id}: el inversor de red que sale solo va con acople en CD.` });
      if (n.precio && !n.solo_respaldo)
        ctx.addIssue({ code: 'custom', path: ['solo_respaldo'], message: `Nivel ${n.id}: con paneles y precio trae su respaldo solo (sin él no se comprueba la suma ni se separa el retorno).` });
    }
    if (n.solo_respaldo && (!n.precio || n.solo_respaldo.precio.contado >= n.precio.contado))
      ctx.addIssue({ code: 'custom', path: ['solo_respaldo'], message: `Nivel ${n.id}: el respaldo solo (sin paneles) tiene que costar menos que el sistema con paneles.` });
    if (n.carga_kw.min >= n.carga_kw.max)
      ctx.addIssue({ code: 'custom', path: ['carga_kw'], message: `Nivel ${n.id}: la carga mínima debe ser menor que la máxima (las horas van en rango, ESPEC 3.4).` });
    if (n.horas.min > n.horas.max) ctx.addIssue({ code: 'custom', path: ['horas'], message: `Nivel ${n.id}: horas al revés.` });
    if (n.bateria.kwh_utiles > n.bateria.kwh_nominales) ctx.addIssue({ code: 'custom', path: ['bateria'], message: `Nivel ${n.id}: los kWh útiles no pueden pasar de los nominales.` });
    if (n.rentabilidad !== undefined && (n.precio === null) !== (n.rentabilidad === null))
      ctx.addIssue({ code: 'custom', path: ['rentabilidad'], message: `Nivel ${n.id}: sin precio no hay rentabilidad, y con precio sí.` });
    if (n.precio === null && (n.bateria.id_tabla !== null && (n.inversor === null || n.inversor.id_tabla !== null)))
      ctx.addIssue({ code: 'custom', path: ['precio'], message: `Nivel ${n.id}: la batería y el inversor están en la tabla y el nivel salió sin precio.` });
    if (n.finsus_referencia) {
      if (!n.precio) ctx.addIssue({ code: 'custom', path: ['finsus_referencia'], message: `Nivel ${n.id}: FINSUS sin precio.` });
      else for (const m of problemasFinsusReferencia(n.finsus_referencia, n.precio.contado, `el nivel ${n.id}`)) ctx.addIssue({ code: 'custom', path: ['finsus_referencia'], message: m });
    }
  });
export type NivelRespaldoCalculado = z.infer<typeof NivelRespaldoCalculado>;

export const ResultadoRespaldo = z
  .strictObject({
    tipo: z.enum(['respaldo', 'hibrido', 'aislado']),
    con_paneles: z.boolean(),
    /** 11-oct: "toda_la_casa" (respaldo e híbrido: niveles por horas con el consumo del recibo) o "cargas" (aislado). Ausente =
     *  resultado de antes del 11-oct (por cargas). */
    alcance: z.enum(['toda_la_casa', 'cargas']).optional(),
    niveles: z.array(NivelRespaldoCalculado).min(1).max(3),
    recomendado: NivelRespaldo,
    /** ADENDA z22-1 (Diego, 16-oct) y 14-oct 22:30: los pares batería + inversor COMPATIBLES de la tabla (con precio y con
     *  garantía en el PDF), para que el vendedor elija UNO para toda la propuesta. Ausente = motor de antes. */
    pares: z.array(z.strictObject({ bateria_id: z.string().min(2).max(40), inversor_id: z.string().min(2).max(40), nombre: z.string().min(3).max(140) })).max(40).optional(),
    /** Híbrido (paneles NUEVOS + baterías), 14-oct, POR CONFIRMAR CON DIEGO: sistema ACOPLADO EN CA. El precio de cada nivel
     *  (`niveles[].precio`) ya es el del SISTEMA: el contado de la opción de paneles `opcion` (la recomendada, la que eligió el
     *  vendedor) + el contado del nivel de baterías con su inversor híbrido, cada uno con sus reglas y su redondeo a $100;
     *  lista = contado ÷ 0.85. Los paneles (su generación y su pago con sistema) son los de esa opción en todos los niveles.
     *  Ausente = no se sumaron paneles (respaldo) o resultado de antes del 14-oct (el PDF híbrido pide recalcular).
     *  Con `por_opcion` (24-oct) cada nivel trae SU sistema (`niveles[].sistema`) y esto es el del nivel recomendado. */
    hibrido: z
      .strictObject({
        opcion: OpcionId,
        /** 'ca' = inversor de red + híbrido acoplado en CA; 'cd' = ADENDA z4 (Diego, 14-oct): UN SOLO inversor híbrido recibe los
         *  paneles directo (caben en su entrada de FV) y el inversor de red de la opción sale; el respaldo cobra solo la
         *  diferencia del híbrido sobre el de red. */
        acople: z.enum(['ca', 'cd']),
        /** true mientras Diego no confirme el acople en CA (las notas internas del PDF lo dicen; el PDF del cliente no). */
        por_confirmar: z.boolean(),
        /** Contado CON IVA de los paneles que se sumó a cada nivel (el de la opción). */
        paneles_contado: Pesos,
        /** Solo con acople 'cd': el inversor de red que sale («1 × Growatt MIN 8000TL-X2»). */
        inversor_red: z.string().min(3).max(120).optional(),
      })
      .optional(),
    /** Baterías POR OPCIÓN (Diego, 24-oct-2026): cada nivel va ligado a su opción (`niveles[].opcion`) y, con paneles, trae
     *  su propio sistema (`niveles[].sistema`). La pantalla y el PDF deciden por esta marca. Ausente = resultado de antes:
     *  un solo juego de paneles (`hibrido`) para todos los niveles. */
    por_opcion: z.literal(true).optional(),
  })
  .superRefine((r, ctx) => {
    const ids = r.niveles.map((n) => n.id);
    // Por opción: o todo el resultado lo dice, o nada (un nivel con su opción en un resultado sin la marca se leería como de
    // antes, con los paneles de la recomendada en todos).
    if (!r.por_opcion && r.niveles.some((n) => n.opcion !== undefined || n.sistema !== undefined || n.horas_objetivo !== undefined || n.noches !== undefined))
      ctx.addIssue({ code: 'custom', path: ['por_opcion'], message: 'El respaldo trae niveles por opción sin por_opcion (se leería como de antes, con los mismos paneles en todos).' });
    if (r.por_opcion) {
      for (const [k, n] of r.niveles.entries())
        if (n.opcion === undefined) ctx.addIssue({ code: 'custom', path: ['niveles', k, 'opcion'], message: `por_opcion: el nivel ${n.id} no dice de qué opción es.` });
      // Con paneles nuevos (híbrido o aislado), CADA nivel trae su sistema: sin él la regla de precio no tendría contra qué
      // comparar y un nivel que cobra solo las baterías pasaría (verificador del 24-oct). Solo respaldo: ninguno (abajo).
      const conSistema = r.niveles.filter((n) => n.sistema).length;
      if (r.con_paneles && r.tipo !== 'respaldo') {
        for (const [k, n] of r.niveles.entries())
          if (!n.sistema) ctx.addIssue({ code: 'custom', path: ['niveles', k, 'sistema'], message: `por_opcion: el nivel ${n.id} lleva paneles nuevos y no trae su sistema (los paneles de su opción).` });
      } else if (conSistema > 0 && conSistema < r.niveles.length)
        ctx.addIssue({ code: 'custom', path: ['niveles'], message: 'por_opcion: todos los niveles traen su sistema o ninguno (no se revuelven niveles con y sin paneles).' });
      if (r.hibrido) {
        const s = r.niveles.find((n) => n.id === r.recomendado)?.sistema;
        if (!s || s.opcion !== r.hibrido.opcion || s.acople !== r.hibrido.acople || (s.inversor_red ?? null) !== (r.hibrido.inversor_red ?? null) || Math.abs(s.paneles_contado - r.hibrido.paneles_contado) > 0.005)
          ctx.addIssue({ code: 'custom', path: ['hibrido'], message: `por_opcion: «hibrido» (${r.hibrido.opcion}) no es el sistema del nivel recomendado (${r.recomendado}).` });
      }
    }
    if ((r.tipo === 'respaldo' || !r.con_paneles) && r.niveles.some((n) => n.sistema))
      ctx.addIssue({ code: 'custom', path: ['niveles'], message: 'Un respaldo sin paneles nuevos no lleva sistema (paneles + baterías es del híbrido o del aislado).' });
    if (new Set(ids).size !== ids.length) ctx.addIssue({ code: 'custom', path: ['niveles'], message: 'Niveles de respaldo repetidos.' });
    if (!ids.includes(r.recomendado)) ctx.addIssue({ code: 'custom', path: ['recomendado'], message: `El nivel recomendado (${r.recomendado}) no está entre los calculados.` });
    if (r.hibrido && r.tipo === 'respaldo')
      ctx.addIssue({ code: 'custom', path: ['hibrido'], message: 'Un respaldo sin paneles nuevos no suma el precio de paneles (eso es del híbrido o del aislado).' });
    // Candado: con paneles sumados, cada nivel con precio cuesta MÁS que los paneles solos (si no, no se sumó la batería).
    // Por opción (24-oct): los paneles son los del sistema del nivel (los de SU opción); si no trae, los de `hibrido`, como antes.
    for (const [k, n] of r.niveles.entries()) {
      const paneles = n.sistema?.paneles_contado ?? r.hibrido?.paneles_contado;
      if (paneles === undefined || !n.precio) continue;
      if (n.precio.contado <= paneles)
        ctx.addIssue({ code: 'custom', path: ['niveles', k, 'precio'], message: `Nivel ${n.id}: el precio del sistema (${n.precio.contado}) no suma las baterías a los paneles (${paneles}).` });
      // Diego, 14-oct 22:30: sistema = paneles (el MISMO precio que la opción sola: de ahí sale el retorno) + respaldo.
      if (n.solo_respaldo && Math.abs(n.precio.contado - (paneles + n.solo_respaldo.precio.contado)) > 0.005)
        ctx.addIssue({ code: 'custom', path: ['niveles', k, 'solo_respaldo'], message: `Nivel ${n.id}: el sistema (${n.precio.contado}) no es paneles (${paneles}) + respaldo (${n.solo_respaldo.precio.contado}).` });
    }
  });
export type ResultadoRespaldo = z.infer<typeof ResultadoRespaldo>;

// ───────────── Resultado del motor ─────────────

/** CANDADO A0 (se activa solo: en cuanto una opción trae la clave finsus_referencia, el resultado habla A0). Vacío =
 *  cuadra. Reglas: todas las opciones traen la clave; FINSUS en todas o en ninguna; cada FINSUS cuadra con su contado;
 *  y todo aviso de FINSUS es nivel "info" (Diego: nada de avisos rojos por FINSUS; "el aviso que quede es informativo y
 *  discreto"). El esquema de ResultadoMotor lo aplica,
 *  así que el motor, el servidor y la interfaz no pueden guardar un resultado A0 que lo rompa. */
export function problemasFinsusA0(r: { opciones: readonly Pick<OpcionCalculada, 'id' | 'precio' | 'finsus_referencia'>[]; avisos: readonly Pick<Aviso, 'codigo' | 'campo' | 'nivel'>[] }): string[] {
  const conClave = r.opciones.filter((o) => o.finsus_referencia !== undefined);
  if (conClave.length === 0) return [];
  const p: string[] = [];
  for (const o of r.opciones)
    if (o.finsus_referencia === undefined) p.push(`Resultado A0 a medias: la opción ${o.id} no trae finsus_referencia (null si FINSUS está apagado).`);
  const valores = conClave.map((o) => o.finsus_referencia);
  if (valores.some((v) => v === null) && valores.some((v) => v !== null))
    p.push('FINSUS va en todas las opciones o en ninguna (formas_de_pago.finsus es de toda la cotización).');
  for (const o of conClave) if (o.finsus_referencia) p.push(...problemasFinsusReferencia(o.finsus_referencia, o.precio.promo?.contado_normal ?? o.precio.contado, `la opción ${o.id}`));
  for (const a of r.avisos)
    if (a.nivel !== 'info' && esAvisoFinsus(a))
      p.push(`ADENDA A0: el aviso ${a.codigo}${a.campo ? ` (${a.campo})` : ''} es de FINSUS y vino "${a.nivel}": FINSUS es solo informativo (nada de rojo ni de amarillo).`);
  return p;
}

// ───────────── Recorte de punta (peak shaving) en GDMTH (ADENDA z54/z55-2, 29-oct-2026) ─────────────

/** Un escenario del ahorro del año del recorte, SIN IVA por renglón y con su IVA (como lo imprime la plantilla horaria). */
export const EscenarioRecorte = z.strictObject({
  capacidad: z.number(),
  distribucion: z.number(),
  energia: z.number(),
  subtotal: z.number(),
  iva: z.number(),
  total: z.number(),
  /** total ÷ 12, redondeado (el promedio al mes, con IVA). */
  mes: z.number(),
});
export type EscenarioRecorte = z.infer<typeof EscenarioRecorte>;

/** Un mes del recorte (las cuentas de formatos/horaria/modelo.mjs; sin IVA). */
export const MesRecorte = z.strictObject({
  etiqueta: z.string().min(1).max(40),
  dias: z.int().min(1).max(70),
  /** Horas de punta del mes (días hábiles y sábados de invierno) y las del día más largo (2 en verano, 4 en invierno). */
  horas_punta: z.number().nonnegative(),
  horas_max: z.number().positive(),
  kwh_punta: z.number().nonnegative(),
  q_kwh: z.number().positive(),
  dp: z.number().positive(),
  dmax: z.number().positive(),
  /** Consumo ÷ (24 · días · 0.57): la demanda de la fórmula de CFE. */
  df: z.number().nonnegative(),
  cap_hoy: z.int().nonnegative(),
  cap_con: z.int().nonnegative(),
  cap_sin: z.int().nonnegative(),
  dist_hoy: z.int().nonnegative(),
  dist_con: z.int().nonnegative(),
  /** Lo que recorta la batería ese mes (kW): mín(recorte, energía al día ÷ horas del día más largo, 0.9 · dp). */
  r: z.number().nonnegative(),
  movidos_kwh: z.number().nonnegative(),
  recorta: z.boolean(),
  manda_punta: z.boolean(),
  a_cap: z.number(),
  a_cap_bajo: z.number(),
  a_dist: z.number(),
  a_ene: z.number(),
  /** Cuotas del periodo (las que revisan el recibo en el PDF). */
  cuotas: CuotasCfe,
  /** true = las cuotas salieron del PROPIO recibo (meses sin cuota 2026 en la tabla del motor). */
  cuotas_del_recibo: z.boolean(),
});
export type MesRecorte = z.infer<typeof MesRecorte>;

export const ESCENARIOS_RECORTE = ['todas', 'fallan2', 'fallan6', 'solo_energia'] as const;
export type EscenarioRecorteId = (typeof ESCENARIOS_RECORTE)[number];
/** Retorno de «más de 25 años» (no se paga en un plazo creíble). */
export const RETORNO_RECORTE_MAS_DE_25 = 99;

/** Recorte de punta: «transformador de 300 kVA» o «2 transformadores de 500 kVA» (UNA copia para el motor, el puente y la pantalla).
 *  Verificación de hoy75: el PDF y la pantalla decían «de 1,000 kVA» con 2 de 500 (el motor sí decía «2 unidades»). */
export function textoTransformador(kva: number, unidades: number | null): string {
  const u = unidades ?? 1;
  return u > 1 ? `${u} transformadores de ${numero(kva / u)} kVA` : `transformador de ${numero(kva)} kVA`;
}

export const ResultadoRecortePunta = z
  .strictObject({
    version: z.literal(1),
    modo: z.literal('solo_bateria'),
    sin_paneles: z.enum(SIN_PANELES_RECORTE),
    /** false = las cuentas no pasan los candados (el aviso rojo RECORTE_NO_SE_OFRECE dice por qué): el PDF no sale. */
    se_ofrece: z.boolean(),
    equipo: z.strictObject({
      id: z.string().min(2).max(60),
      marca: Texto(2, 40),
      modelo: Texto(2, 60),
      gabinetes: z.int().min(1).max(RECORTE_PUNTA_REGLAS.gabinetes_max),
      kw_pcs: z.number().positive(),
      kwh_nominales: z.number().positive(),
      /** A la descarga recomendada (90 %). */
      kwh_utiles: z.number().positive(),
      /** Energía que entrega cada día en CA en el año 1 (descarga × salud del año 1 × √eficiencia). */
      kwh_ca_dia: z.number().positive(),
      eficiencia: z.number().min(0.7).max(0.98),
      quimica: z.enum(['LFP', 'NMC']),
      garantia_anios: z.int().min(1).max(30),
      ciclos: z.int().positive(),
      /** Capacidad ESPERADA al final de la garantía (tabla de salud de la cotización del fabricante; no garantizada). */
      salud_final_pct: z.number().min(50).max(100),
      /** kVA del transformador (servicio de 220 V o sin contestar); null = sin transformador (440/480 V). Con varias unidades, el total. */
      transformador_kva: z.number().positive().nullable(),
      /** Cuántos transformadores suman esos kVA (arriba de 500 kVA van varios del más grande); null = sin transformador. Verificación
       *  de hoy75: el PDF decía «Transformador de 1,000 kVA» con 2 de 500 (el motor sí decía «2 unidades»). */
      transformador_unidades: z.int().min(1).nullable(),
      cotizado_el: Fecha,
      vence_el: Fecha,
      /** La cotización del fabricante venció: el precio del equipo queda POR CONFIRMAR (el PDF lo dice) y el retorno no pasa de
       *  amarillo. */
      vencido: z.boolean(),
      /** De dónde sale el costo del gabinete: su cotización, o (vencida) el gabinete equivalente más barato de la lista del mes si
       *  es más caro (z55-4, lo conservador). */
      precio_de: z.enum(['cotizacion', 'lista']),
    }),
    /** El recorte que se manda a la plantilla (kw_recorte): mín(PCS, energía al día ÷ 2, 0.9 · la punta más alta), al décimo. */
    kw_recorte: z.number().positive(),
    /** «Recortamos de N₁ a N₂ kW» (z34-5): el menor y el mayor recorte de los meses con punta, hacia abajo. */
    kw_temporada: z.strictObject({ min: z.int().nonnegative(), max: z.int().positive() }),
    meses: z.array(MesRecorte).length(12),
    meses_que_recortan: z.int().min(0).max(12),
    meses_manda_punta: z.int().min(0).max(12),
    /** Zona muerta promedio (kW de punta − Df cuando es positiva), de los meses que la tienen; 0 si ninguno. */
    zona_muerta_kw: z.number().nonnegative(),
    /** Cuotas con que se cuenta el AHORRO todo el año: las del último mes publicado (candado 12). */
    cuotas_ahorro: CuotasCfe,
    cuotas_ahorro_mes: z.string().regex(/^\d{4}-\d{2}$/),
    escenarios: z.strictObject({ todas: EscenarioRecorte, fallan2: EscenarioRecorte, fallan6: EscenarioRecorte, solo_energia: EscenarioRecorte }),
    /** Años, de contado con IVA y con la luz +6 %/año y la pérdida de capacidad de la batería (aniosRetorno), al décimo.
     *  RETORNO_RECORTE_MAS_DE_25 = más de 25. `todas_sin_alza`: sin que suba la luz (para el aviso). */
    retorno: z.strictObject({ todas: z.number().positive(), fallan2: z.number().positive(), fallan6: z.number().positive(), solo_energia: z.number().positive(), todas_sin_alza: z.number().positive() }),
    /** verde = ≤ 4.0 años con todas las puntas y ≤ 5.0 con 2 meses fallados (sin redondear) y precio VIGENTE; amarillo = hasta 5.0
     *  (o un precio por confirmar); null = no se ofrece. */
    semaforo_retorno: z.enum(['verde', 'amarillo']).nullable(),
    precio: PrecioRespaldo,
    /** Solo quien ve costos (vista.ts la quita; a los internos les deja solo el color en semaforo_margen). */
    rentabilidad: Rentabilidad.optional(),
    semaforo_margen: Semaforo.optional(),
    finsus_referencia: FinsusReferencia.nullable(),
  })
  .superRefine((x, ctx) => {
    const R = RECORTE_PUNTA_REGLAS;
    const add = (path: (string | number)[], message: string) => ctx.addIssue({ code: 'custom', path, message });
    const E = x.equipo.kwh_ca_dia;
    if (E < 2 * x.kw_recorte - 1e-6) add(['kw_recorte'], `Recorte de punta: con ${E} kWh al día no sostiene ${x.kw_recorte} kW las 2 h de la punta de verano.`);
    if (E > x.equipo.kwh_utiles + 1e-6) add(['equipo', 'kwh_ca_dia'], 'Recorte de punta: la energía al día pasa de los kWh útiles.');
    if (x.kw_recorte > x.equipo.kw_pcs * x.equipo.gabinetes + 1e-6) add(['kw_recorte'], 'Recorte de punta: el recorte pasa de la potencia de los gabinetes.');
    x.meses.forEach((m, k) => {
      if (m.r > R.tope_pct_punta * m.dp + 1e-6) add(['meses', k, 'r'], `Recorte de punta, ${m.etiqueta}: recorta ${m.r} kW, más de ${R.tope_pct_punta} × su punta (${m.dp} kW).`);
      if (m.r > E / m.horas_max + 1e-6 || m.r > x.kw_recorte + 1e-6) add(['meses', k, 'r'], `Recorte de punta, ${m.etiqueta}: recorta más de lo que sostiene la batería toda la punta.`);
      if (m.dist_con < m.dist_hoy) add(['meses', k, 'dist_con'], `Recorte de punta, ${m.etiqueta}: la distribución nunca se promete a la baja (candado 6).`);
    });
    if (x.meses.filter((m) => m.recorta).length !== x.meses_que_recortan) add(['meses_que_recortan'], 'Recorte de punta: los meses que recortan no cuadran con los meses.');
    const e = x.escenarios;
    for (const [id, s] of Object.entries(e)) {
      if (s.capacidad + s.distribucion + s.energia !== s.subtotal || s.subtotal + s.iva !== s.total) add(['escenarios', id], `Recorte de punta: el escenario ${id} no suma.`);
    }
    // (si no se ofrece puede no recortar nada: todas = solo energía)
    if (!(e.todas.total >= e.fallan2.total && e.fallan2.total >= e.fallan6.total && e.fallan6.total >= e.solo_energia.total && (!x.se_ofrece || e.todas.total > e.solo_energia.total)))
      add(['escenarios'], 'Recorte de punta: el ahorro tiene que bajar de «todas las puntas» a «2 meses fallados», «6 meses» y «solo energía».');
    const r = x.retorno;
    if (!(r.todas <= r.fallan2 && r.fallan2 <= r.fallan6 && r.fallan6 <= r.solo_energia && r.todas <= r.todas_sin_alza))
      add(['retorno'], 'Recorte de punta: los retornos tienen que ir al revés que el ahorro.');
    if (x.se_ofrece && (x.meses_que_recortan < R.meses_minimos || r.todas > R.retorno_tope + 1e-9))
      add(['se_ofrece'], `Recorte de punta: solo se ofrece si recorta en ${R.meses_minimos} de 12 meses o más y se paga en ${R.retorno_tope} años o menos.`);
    const verde = r.todas <= R.retorno_verde + 1e-9 && r.fallan2 <= R.retorno_tope + 1e-9;
    if (x.se_ofrece !== (x.semaforo_retorno !== null)) add(['semaforo_retorno'], 'Recorte de punta: el semáforo de retorno va solo cuando se ofrece.');
    // (el motor decide con el retorno SIN redondear: un 4.04 se guarda «4.0» y queda amarillo; aquí solo se revisa que un verde lo sea)
    if (x.semaforo_retorno === 'verde' && (!verde || x.equipo.vencido)) add(['semaforo_retorno'], `Recorte de punta: verde es ≤ ${R.retorno_verde} años con todas las puntas y ≤ ${R.retorno_tope} con 2 meses fallados, con el precio del equipo vigente.`);
    if (x.equipo.precio_de === 'lista' && !x.equipo.vencido) add(['equipo', 'precio_de'], 'Recorte de punta: el precio de la lista del mes solo se usa con la cotización vencida.');
    if (x.precio.lista !== x.precio.contado || x.precio.descuento_contado_pct !== 0) add(['precio'], 'Recorte de punta: precio único (lista = contado, sin descuento).');
    if (x.kw_temporada.max < x.kw_temporada.min) add(['kw_temporada'], 'Recorte de punta: N₁ no puede ser mayor que N₂.');
  });
export type ResultadoRecortePunta = z.infer<typeof ResultadoRecortePunta>;

export const ResultadoMotor = z
  .strictObject({
    calculado_en: FechaHora,
    version_motor: Texto(1, 40),
    tarifa: Tarifa,
    facturacion: Facturacion,
    /** IVA que CFE cobra en el recibo de la ciudad de la obra (ivaCfe: 8 en la franja fronteriza, 16 en el resto). La
     *  cotización va siempre al 16 % (ADENDA); esto solo cuadra el recibo en la plantilla (recibo.iva_pct, N9). L2, 27-oct.
     *  Ausente en los cálculos de antes. */
    iva_cfe_pct: z.number().positive().max(100).optional(),
    /** Con baterías (E5b): las opciones que llevan el MÍNIMO de paneles que recibe el híbrido (z28-9; pueden cubrir hasta
     *  COBERTURA_LIMITE.max). Ausente si ninguna. L2, 27-oct (HIB-02). */
    paneles_minimo_hibrido: z.array(OpcionId).min(1).max(3).optional(),
    periodos: z.array(PeriodoCalculo).min(1).max(12),
    opciones: z.array(OpcionCalculada).min(1).max(3),
    recomendada: OpcionId,
    avisos: z.array(Aviso),
    /** Supuestos escritos ("generación con el perfil de Hermosillo de PVGIS, pérdidas 14 %"). */
    supuestos: z.array(z.string().min(3).max(300)),
    /** Contrato v2 · baterías (tipo "baterias"/"aislado" con cuestionario.respaldo). Ausente/null = no aplica. */
    respaldo: ResultadoRespaldo.nullable().optional(),
    /** Etapa "Conectar" · variante dos_medidores de la familia: los periodos de CADA medidor tal como se consolidaron
     *  (cada uno con SUS fechas), antes de sumarlos mes por mes. El PDF los imprime apilados y suma él mismo (la
     *  plantilla truena si recibe los periodos ya sumados). Ausente = motor de antes; null = un solo medidor. */
    medidores: z.strictObject({ A: z.array(PeriodoCalculo).min(1).max(12), B: z.array(PeriodoCalculo).min(1).max(12) }).nullable().optional(),
    /** Versión de la tabla de precios con la que se calculó. El servidor no debe entregar un PDF calculado con una tabla
     *  que ya no es la vigente (hallazgo del 10-oct). Ausente = motor de antes. */
    tabla_precios_version: z.int().min(1).optional(),
    /** VERDE DE ENTRADA: lo que subió el mínimo en cada opción, nivel y recorte de este cálculo (contado CON IVA, 0 = la tabla ya llegaba). De aquí
     *  sale `parametros.verde_congelado` al generar el PDF. Ausente = sin la regla. Solo lo ve Diego (vista.ts). */
    minimos_verde: z.record(z.string().min(1).max(120), z.number().min(0)).optional(),
    /** ADENDA A0b: el tipo de cambio con que se calcularon los precios (el congelado del folio). Ausente = tabla en pesos. */
    tipo_cambio: TipoCambioFolio.optional(),
    /** Diego, 12-oct: la estructura con que se costeó y se puso precio (PTR, suelo y carport suben el precio del sistema
     *  con lo que cuestan de más). `por_confirmar` = su mano de obra o su costo no los ha dado Diego (aviso amarillo).
     *  Sin cifras de costo: lo ven todos. Ausente = motor de antes (losa/lámina). */
    estructura: z.strictObject({ tipo: z.enum(TIPOS_ESTRUCTURA), por_confirmar: z.boolean() }).optional(),
    /** Diego, 12-oct: el servicio de CFE con que se eligieron los inversores (fases y tensión). `deducido` = el cuestionario
     *  no lo dijo y salió de la tarifa, los hilos del recibo o el tamaño (confirmar en la visita); `como` lo explica en
     *  palabras. Ausente = motor de antes. */
    servicio: z
      .strictObject({ fases: z.enum(FASES_SERVICIO), tension: z.enum(TENSIONES_SERVICIO), deducido: z.boolean(), como: Texto(3, 200) })
      .optional(),
    /** Etapa 3 (Diego, 12-oct, puntos 2 y 3): la generación por kW con que se calculó TODO lo prometido (la base de Diego o la
     *  que eligió el vendedor para esta obra) y el sol del municipio de la obra. Ausente = motor de antes (la base). */
    generacion: GeneracionCalculada.optional(),
    /** Diego, 12-oct noche (punto a): true = en GDMTO/GDMTH el ahorro (y todo lo prometido: pago con sistema, retorno, 25
     *  años) INCLUYE la baja de capacidad y distribución con la fórmula de CFE, techo(kWh netos ÷ (24 · días · FC)) contra la
     *  demanda medida de cada mes (año típico, con la bolsa de 12 meses ya formada). Cada opción trae entonces
     *  `kwh_facturados` y `ahorro_demanda`. false = solo energía (falta la demanda medida de algún mes: aviso). Ausente =
     *  motor de antes u otra tarifa. */
    demanda_cfe: z.boolean().optional(),
    /** ADENDA z27-4 (Diego, 22-oct): casa con recibo que YA pasa el límite DAC de su tarifa doméstica y que con la
     *  recomendada queda debajo. `pago_dac_mes` = lo que pagaría al mes en DAC con SU consumo (cuotas DAC vigentes, con IVA
     *  y alumbrado); `con_sistema_kwh_mes` = el promedio que CFE le factura con la recomendada. null = no aplica; ausente =
     *  motor de antes. El PDF imprime con esto la línea de urgencia. */
    /** RECORTE DE PUNTA (ADENDA z54/z55-2, 29-oct-2026): la batería grande sola en GDMTH (cuestionario.recorte_punta con
     *  parametros.recorte_punta = 1). Con él, las opciones de paneles son de REFERENCIA (no se venden ni van al PDF). null = se pidió
     *  y no se pudo calcular (el rojo RECORTE_NO_SE_OFRECE dice por qué); ausente = no se pidió (o motor de antes). */
    recorte_punta: ResultadoRecortePunta.nullable().optional(),
    riesgo_dac: z
      .strictObject({
        tarifa: Tarifa,
        limite_kwh_mes: z.number().positive(),
        promedio_kwh_mes: z.number().positive(),
        pago_dac_mes: z.number().positive(),
        con_sistema_kwh_mes: z.number().min(0),
      })
      .refine((x) => x.promedio_kwh_mes > x.limite_kwh_mes && x.con_sistema_kwh_mes <= x.limite_kwh_mes, 'riesgo_dac: el consumo de hoy pasa el límite y con el sistema queda debajo')
      .nullable()
      .optional(),
    /** ADENDA z52 (Diego, 28-oct-2026): el cálculo NO usa el recibo tal cual. `recibo_real` = lo que dice el recibo (su tarifa,
     *  sus kWh y lo que pagó en el año: se imprime en UNA línea y nunca se resta de una cifra en otra tarifa); `cambio_tarifa` =
     *  negocio con recibo de casa calculado en PDBT/GDBT (tarifa-futura.ts); `aumento` = kWh de más del aumento de consumo que
     *  aceptó el vendedor (cuestionario.consumo.aumento). Ausente = el cálculo es el recibo, como siempre. */
    base_futura: z
      .strictObject({
        /** `pagos_estimados` (verificación del 28-oct): cuántos de los pagos del año NO trae el recibo (se estimaron con las cuotas de
         *  CFE); con alguno, la propuesta no dice «Hoy pagas» como si fuera del recibo. Ausente = 0. */
        recibo_real: z.strictObject({ tarifa: Tarifa, kwh_anual: z.number().nonnegative(), pago_anual: Pesos, pagos_estimados: z.number().int().nonnegative().optional() }),
        cambio_tarifa: z.strictObject({ de: Tarifa, a: z.enum(['PDBT', 'GDBT']) }).nullable(),
        aumento: z.strictObject({ kwh_anual: z.number().positive(), motivo: z.string().trim().min(3).max(40), fuente: z.enum(['ia', 'ia_simulada', 'reglas', 'vendedor']) }).nullable(),
      })
      .refine((x) => x.cambio_tarifa !== null || x.aumento !== null, 'base_futura: sin cambio de tarifa ni aumento, el cálculo es el recibo (no va base_futura)')
      .optional(),
  })
  .superRefine((r, ctx) => {
    for (const m of problemasFinsusA0(r)) ctx.addIssue({ code: 'custom', path: ['opciones'], message: m });
    if (r.recorte_punta && r.tarifa !== 'GDMTH') ctx.addIssue({ code: 'custom', path: ['recorte_punta'], message: `El recorte de punta es solo GDMTH (la tarifa es ${r.tarifa}).` });
    // Baterías por opción (24-oct): los paneles del sistema de cada nivel son los de SU opción, al mismo contado (de ahí sale
    // el retorno). Fallo ruidoso: un nivel que sumó los paneles de otra opción no se guarda.
    // Y el nivel recomendado es el de la opción recomendada (si no, la pantalla y el CRM cobrarían otra combinación).
    if (r.respaldo?.por_opcion && r.respaldo.con_paneles && r.respaldo.tipo !== 'respaldo' && r.respaldo.recomendado !== NIVEL_DE_OPCION[r.recomendada])
      ctx.addIssue({ code: 'custom', path: ['respaldo', 'recomendado'], message: `por_opcion: el nivel recomendado (${r.respaldo.recomendado}) no es el de la opción recomendada (${r.recomendada} → ${NIVEL_DE_OPCION[r.recomendada]}).` });
    r.respaldo?.niveles.forEach((n, k) => {
      if (!n.sistema) return;
      const o = r.opciones.find((x) => x.id === n.sistema!.opcion);
      if (!o) ctx.addIssue({ code: 'custom', path: ['respaldo', 'niveles', k, 'sistema'], message: `Nivel ${n.id}: la opción ${n.sistema.opcion} no está entre las calculadas.` });
      else if (Math.abs(o.precio.contado - n.sistema.paneles_contado) > 0.005)
        ctx.addIssue({ code: 'custom', path: ['respaldo', 'niveles', k, 'sistema'], message: `Nivel ${n.id}: los paneles del sistema (${n.sistema.paneles_contado}) no son los de la opción ${o.id} (${o.precio.contado}).` });
    });
    // Demanda con la fórmula de CFE: solo GDMTO/GDMTH, y entonces cada opción trae sus kWh facturados (uno por periodo) y la
    // parte del ahorro que es demanda (no más que el ahorro). Fallo ruidoso: un resultado a medias no se guarda.
    if (r.demanda_cfe) {
      if (r.tarifa !== 'GDMTO' && r.tarifa !== 'GDMTH') ctx.addIssue({ code: 'custom', path: ['demanda_cfe'], message: `demanda_cfe solo aplica en GDMTO y GDMTH (la tarifa es ${r.tarifa}).` });
      r.opciones.forEach((o, k) => {
        if (!o.kwh_facturados || o.kwh_facturados.length !== r.periodos.length)
          ctx.addIssue({ code: 'custom', path: ['opciones', k, 'kwh_facturados'], message: `Opción ${o.id}: con demanda_cfe trae los kWh facturados de los ${r.periodos.length} periodos.` });
        else if (o.kwh_facturados.some((q, i) => q > r.periodos[i]!.consumo_kwh + 0.5))
          ctx.addIssue({ code: 'custom', path: ['opciones', k, 'kwh_facturados'], message: `Opción ${o.id}: un periodo factura más kWh con el sistema que hoy.` });
        if (o.ahorro_demanda === undefined || o.ahorro_demanda > Math.max(0, o.ahorro_anual) + 0.5)
          ctx.addIssue({ code: 'custom', path: ['opciones', k, 'ahorro_demanda'], message: `Opción ${o.id}: con demanda_cfe trae la parte del ahorro que es demanda, sin pasar del ahorro del año.` });
      });
    } else if (r.opciones.some((o) => o.kwh_facturados !== undefined))
      ctx.addIssue({ code: 'custom', path: ['opciones'], message: 'kwh_facturados solo va con demanda_cfe.' });
    if (r.medidores) {
      const { A, B } = r.medidores;
      if (A.length !== r.periodos.length || B.length !== r.periodos.length)
        ctx.addIssue({ code: 'custom', path: ['medidores'], message: `Dos medidores: cada medidor trae ${A.length} y ${B.length} periodos y la suma ${r.periodos.length}; deben ser los mismos.` });
      else
        r.periodos.forEach((p, k) => {
          const c = A[k]!.consumo_kwh + B[k]!.consumo_kwh;
          const $ = A[k]!.pago_hoy + B[k]!.pago_hoy;
          if (Math.abs(c - p.consumo_kwh) > 0.02 || Math.abs($ - p.pago_hoy) > 0.02)
            ctx.addIssue({ code: 'custom', path: ['medidores'], message: `Dos medidores: en ${p.etiqueta} A + B no da la suma del periodo (consumo ${c} contra ${p.consumo_kwh}, pago ${$} contra ${p.pago_hoy}).` });
        });
    }
  });
export type ResultadoMotor = z.infer<typeof ResultadoMotor>;

/** Lo que el GENERADOR imprimió de FINSUS (lo saca el puente del modelo que devuelve generarFamilia). Sirve para
 *  comprobar que las dos copias de la fórmula (motor y plantilla) dicen lo mismo. */
export const FinsusImpreso = z.strictObject({
  tasa_anual_pct: z.number().positive().max(80),
  /** El CAT que imprimió la leyenda chica (null = la leyenda va sin CAT; ausente = el puente no lo sabe). */
  cat_promedio_pct: z.number().positive().max(80).nullable().optional(),
  por_opcion: z
    .array(z.strictObject({ opcion: OpcionId, enganche: z.number().positive(), mensualidad: z.number().positive() }))
    .max(3)
    .refine((xs) => new Set(xs.map((x) => x.opcion)).size === xs.length, 'una opción repetida'),
});
/** Esquema (no solo tipo) desde la integración del 10-oct-2026: el servidor lo valida con él antes de comparar. */
export type FinsusImpreso = z.infer<typeof FinsusImpreso>;

/** Diferencias entre el FINSUS que calculó el motor y el que imprimió el PDF (± $1; tasa exacta). Vacío = cuadran.
 *  Si no cuadran, el servidor NO entrega el PDF (422 PDF_FALLO): el cliente vería una mensualidad y el vendedor otra. */
export function diferenciasFinsusImpreso(opciones: readonly Pick<OpcionCalculada, 'id' | 'finsus_referencia'>[], impreso: FinsusImpreso): string[] {
  // Motor de antes de A0 (ninguna opción trae la clave): no hay contra qué comparar.
  if (opciones.every((o) => o.finsus_referencia === undefined)) return [];
  const d: string[] = [];
  const porId = new Map(impreso.por_opcion.map((x) => [x.opcion, x] as const));
  for (const o of opciones) {
    const f = o.finsus_referencia;
    const x = porId.get(o.id);
    if (!f) {
      if (x) d.push(`El PDF imprime FINSUS en la opción ${o.id} y el cotizador no lo calculó.`);
      continue;
    }
    if (!x) {
      d.push(`El cotizador calculó FINSUS para la opción ${o.id} y el PDF no lo imprimió.`);
      continue;
    }
    if (Math.abs(x.mensualidad - f.mensualidad) > 1) d.push(`FINSUS de la opción ${o.id}: el PDF dice ${x.mensualidad} al mes y el cotizador ${f.mensualidad}.`);
    if (Math.abs(x.enganche - f.enganche) > 1) d.push(`FINSUS de la opción ${o.id}: el PDF dice enganche ${x.enganche} y el cotizador ${f.enganche}.`);
    if (Math.abs(impreso.tasa_anual_pct - f.tasa_anual_pct) > 1e-9) d.push(`FINSUS: el PDF usa tasa ${impreso.tasa_anual_pct} % y el cotizador ${f.tasa_anual_pct} %.`);
    if (impreso.cat_promedio_pct !== undefined && (impreso.cat_promedio_pct === null) !== (f.cat_promedio_pct === null))
      d.push(`FINSUS: la leyenda del PDF ${impreso.cat_promedio_pct === null ? 'no trae' : 'trae'} CAT y el cotizador ${f.cat_promedio_pct === null ? 'no lo tiene' : 'sí'}.`);
    else if (typeof impreso.cat_promedio_pct === 'number' && f.cat_promedio_pct !== null && Math.abs(impreso.cat_promedio_pct - f.cat_promedio_pct) > 0.05)
      d.push(`FINSUS: el PDF imprime CAT ${impreso.cat_promedio_pct} % y el cotizador ${f.cat_promedio_pct} %.`);
  }
  for (const x of impreso.por_opcion) if (!opciones.some((o) => o.id === x.opcion)) d.push(`El PDF imprime FINSUS de la opción ${x.opcion}, que el cotizador no calculó.`);
  return [...new Set(d)];
}
